import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  NgZone,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import CustomStore from 'devextreme/data/custom_store';
import { toObservable, toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DX_USD_CURRENCY_FORMAT, Entity } from '@ag-common-lib/public-api';
import { type DataType, type SortOrder, type ValidationCallbackData } from 'devextreme/common';
import { type Format } from 'devextreme/common/core/localization';
import { type FixedPosition, type SelectedFilterOperation } from 'devextreme/common/grids';
import { DxFormComponent } from 'devextreme-angular';
import { type ValueChangedEvent } from 'devextreme/ui/date_box';
import {
  ReportFilterOptionsPages,
  ReportingComparisonDatasetSelections,
  ReportingComparisonDatasets,
  ReportingComparisonRowLevels,
  ReportingComparisonScopes,
  buildComparisonSummaryCards,
  findComparisonTable,
  formatComparisonCompactDateRange,
  formatComparisonCurrency,
  formatComparisonCurrencyShort,
  formatComparisonDecimal,
  formatComparisonDeltaCurrency,
  formatComparisonDeltaNumber,
  formatComparisonPercent,
  getComparisonAllTable,
  getComparisonColumnCaption,
  getComparisonDatasetTitle,
  getComparisonMetricCaption,
  getComparisonMoverTable,
  getComparisonMoverTitle,
  getComparisonOverallTable,
  getComparisonPdfBlocks,
  getComparisonPercentCaption,
  hasComparisonAgentMovers,
  hasComparisonMoverTables,
  isComparisonPdfBlockAvailable,
  COMPARISON_DEFAULT_FILTER_OPERATION,
  type ComparisonColumnField,
  type ComparisonColumnKind,
  type ComparisonRanges,
  type ComparisonTableDefinition,
  type ReportingComparisonRowDto,
  type ReportingComparisonDatasetDto,
  type ReportingComparisonHttpQuery,
  type ReportingComparisonResponseDto,
  type ReportingComparisonScope,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  firstValueFrom,
  from,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  getReportUnavailableReason,
  type ReportUnavailableReason,
} from '../../../components/report-unavailable/report-unavailable.component';
import { ReportFilterOptionsService } from '../../../services/report-filter-options.service';
import {
  getReportFilterPlaceholder,
  toReportFilters,
  type ReportFilterOption,
} from '../../../utils/report-filter-options.utils';
import { REPORT_SCOPE_LABELS, canSeeReportScope, parseReportScopeParam } from '../../../utils/report-sections.utils';
import { ModalWindowComponent } from '../../../components/modal-window/modal-window.component';
import { PRODUCTION_DASHBOARD_CONTEXT } from '../production-dashboard-context';
import {
  CHART_DATASET_OPTIONS,
  COMPARISON_MODE_OPTIONS,
  COMPARISON_COLUMN_WIDTHS,
  COMPARISON_DASHBOARD_REPORT_ID,
  buildComparisonSettingsId,
  COMPARISON_HIDEABLE_COLUMNS,
  COMPARISON_NAME_COLUMN_MIN_WIDTH,
  QUARTER_OPTIONS,
  type ChartDatasetKey,
  type ComparisonKpiCard,
  type ComparisonPresetMode,
  type ComparisonSetupState,
  type QuarterValue,
  type SelectOption,
  BlockDatasetOptions,
} from './production-comparison-dashboard.models';
import {
  ProductionComparisonExportService,
  type ProductionComparisonExportSheet,
  type ProductionComparisonExportTablePair,
} from './production-comparison-export.service';
import {
  ProductionComparisonExportBuilderService,
  type ComparisonExportContext,
} from './production-comparison-export-builder.service';
import { ProductionComparisonReportService } from './production-comparison-report.service';
import { ProductionComparisonRowsService } from './production-comparison-rows.service';
import { createComparisonMoverStore, createComparisonRowsStore } from './production-comparison-rows.store';
import {
  type ComparisonPdfBlockGroup,
  type ComparisonPdfRequestBase,
} from './production-comparison-pdf-preview/production-comparison-pdf-preview.component';
import {
  applyComparisonGridDiff,
  buildComparisonEffectiveDefault,
  buildComparisonGridDiff,
  isComparisonGridEntryEmpty,
  isComparisonGridLayoutEmpty,
  type ComparisonGridState,
} from './production-comparison-grid-state.util';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import {
  ReportGridSettingsKeys,
  type ReportGridPreset,
  type ReportGridSettings,
  type ReportGridSettingsEntry,
} from 'ag-common-lib/lib/models/reporting/report-grid-settings.model';
import { AuthService } from '../../../services/auth.service';
import { ReportGridSettingsService } from '../../../services/report-grid-settings.service';
import {
  COMPARISON_ALL_TABLE_DEFAULT_PAGE_SIZE,
  COMPARISON_ALL_TABLE_PAGE_SIZES,
  COMPARISON_TABLE_PAGE_SIZES,
  ComparisonTableState,
  type ComparisonTableDirection,
  type ComparisonTablePersistedConfig,
} from './production-comparison-table.state';
import {
  buildIsoDate,
  getEndOfYearDate,
  getLastPassedFriday,
  getStartOfYearDate,
  normalizeAsOfDate,
  quarterFromIsoDate,
  quarterRange,
  shiftIsoDateByYears,
  toDateParts,
  toIsoDate,
} from 'ag-common-svc/lib/utils/reporting-comparison-date.utils';

type MoverModalRow = ReportingComparisonRowDto & {
  changeCategory: 'increase' | 'decrease';
};

type DateRangeBoxValue = Date | string | number | null | undefined;

/**
 * Clears the layouts the page used to keep in this browser.
 *
 * They are not carried over on purpose. They are whole-state snapshots, so importing one would
 * turn every property it pins — including the filter operation it recorded before the default
 * moved to ">=" — into an explicit override nobody chose and nothing could later change. That
 * is the problem being removed, so it is not worth preserving one last time. Everyone's tables
 * open at their defaults once; from then on settings follow the viewer and survive.
 */
function discardLegacyLocalGridState(): void {
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith('ag.comparison.'))
      .forEach(key => localStorage.removeItem(key));
  } catch {
    // Private browsing and blocked site data both throw; there is nothing to clean either way.
  }
}

/** The slice of a DevExtreme grid this page drives directly. */
type ComparisonGridHost = {
  state?: (value?: unknown) => unknown;
  showColumnChooser?: () => void;
  columnOption?: (id: string, option?: string, value?: unknown) => unknown;
};

/**
 * How the page is arranged and whom it is narrowed to, with no period in it.
 *
 * What a preset holds, and what Default stands for.
 */
interface ComparisonArrangement {
  hiddenColumns?: string[];
  grids?: ReportGridSettingsEntry[];
  mgaTopCount?: number;
  agentTopCount?: number;
  reportFilterValues?: string[];
}

/**
 * One column as a grid template declares it, worked out from the table catalog.
 *
 * Every option is given a value, the defaults included: a column that declared nothing and one
 * bound to `undefined` are not the same thing to DevExtreme for every option, and these grids'
 * defaults are what every stored arrangement is a difference from.
 */
interface ComparisonGridColumnView {
  field: string;
  caption: string;
  width: number | undefined;
  minWidth: number | undefined;
  dataType: DataType | undefined;
  format: Format | undefined;
  customizeText: ((cellInfo: { value?: number | null }) => string) | undefined;
  selectedFilterOperation: SelectedFilterOperation | undefined;
  sortOrder: SortOrder | undefined;
  allowHiding: boolean;
  allowSorting: boolean;
  allowFiltering: boolean;
  allowHeaderFiltering: boolean;
  fixed: boolean;
  fixedPosition: FixedPosition | undefined;
}

/** What the PDF preview is opened with: the page as it was, and what can be printed from it. */
interface ComparisonPdfPreviewState {
  request: ComparisonPdfRequestBase;
  blockGroups: ComparisonPdfBlockGroup[];
  fileName: string;
}

const COMPARISON_DECIMAL_FORMAT: Format = { type: 'fixedPoint', precision: 1 };

@Component({
  selector: 'ag-portal-production-comparison-dashboard',
  templateUrl: './production-comparison-dashboard.component.html',
  styleUrls: ['./production-comparison-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductionComparisonDashboardComponent {
  private static readonly defaultTopCount = 15;
  /** Set on the expanded card itself; see reserveRoomForOverlappingChrome. */
  private static readonly fullScreenTopProperty = '--comparison-full-screen-top';
  /** Stands for the page as it ships, so the picker always has something selected. */
  private static readonly defaultPresetId = '__default__';

  @ViewChild('setupFormRef', { static: false }) private setupFormComponent?: DxFormComponent;

  protected readonly setupValidationGroup = 'productionComparisonSetupValidationGroup';
  protected readonly comparisonModeOptions = COMPARISON_MODE_OPTIONS;
  protected readonly quarterOptions = QUARTER_OPTIONS;
  protected readonly chartDatasetOptions = CHART_DATASET_OPTIONS;
  protected readonly topCountOptions: SelectOption<number>[] = COMPARISON_TABLE_PAGE_SIZES.map(value => ({
    id: value,
    text: `${value}`,
  }));

  /**
   * One state per mover table, keyed by dataset, level and direction.
   *
   * Each table pages itself against /reports/comparison/rows, so growing one does not refetch
   * the comparison, its charts and its totals — and no table is bound by the page-wide top
   * count the embedded movers were cut to.
   */
  private readonly moverTableStates = new Map<string, ComparisonTableState>();
  /** Each mover grid's widget, so its own load panel can be shown while a page is in flight. */
  private readonly moverGrids = new Map<
    string,
    { beginCustomLoading: (text: string) => void; endCustomLoading: () => void; refresh?: () => unknown }
  >();
  /** Every grid that saves a layout, by its storage key, so Reset and the column picker can reach it. */
  private readonly gridsByGridId = new Map<string, ComparisonGridHost>();
  /**
   * Columns hidden across the whole page, by field.
   *
   * A grid's own picker sets that grid alone; this one sets every table at once, which is what
   * anyone reading the page actually wants when a column is of no interest to them — there are
   * twelve tables and they all carry the same figures.
   */
  private readonly hiddenColumns = new Set<string>();
  /** The viewer's saved arrangements, and which of them is loaded. */
  /**
   * The viewer's presets for this report and this view, read straight from the document.
   *
   * Not a copy kept in step with it. Firestore reports a write of ours before the server has
   * it, so saving a preset shows up at once and nothing here has to be told about it — which is
   * what a second copy was for, and what kept going wrong.
   */
  protected readonly presetsSig = computed<ReportGridPreset[]>(() => {
    const stored = this.settingsSnapshotSig()?.settings?.[ReportGridSettingsKeys.presets];

    return Array.isArray(stored) ? stored : [];
  });

  /** Which preset this report opens on; null means it opens on what it ships with. */
  protected readonly defaultPresetIdSig = computed<string | null>(
    () => this.settingsSnapshotSig()?.settings?.[ReportGridSettingsKeys.defaultPresetId] ?? null,
  );

  /** Which preset is on screen. The viewer's choice, so it is the page's and not the document's. */
  protected readonly activePresetIdSig = signal<string>(ProductionComparisonDashboardComponent.defaultPresetId);

  protected readonly activePresetSig = computed<ReportGridPreset | null>(
    () => this.presetsSig().find(preset => preset.id === this.activePresetIdSig()) ?? null,
  );

  /** The picker's rows: the page as it ships, then whatever the viewer has saved. */
  protected readonly presetItemsSig = computed(() => [
    { id: ProductionComparisonDashboardComponent.defaultPresetId, name: 'Default' },
    ...this.presetsSig().map(preset => ({ id: preset.id, name: preset.name })),
  ]);
  /**
   * Save and Save-as-new live in the picker itself rather than beside it.
   *
   * `dropDown` is listed because naming any button replaces the editor's own set, and leaving
   * it out would take the arrow away.
   */
  /** Whether the page differs from the arrangement the picker is naming. */
  protected readonly hasArrangementChangesSig = signal(false);
  /**
   * The arrangement Save and + are judged against: what was on the page once it had finished
   * loading, or what was last applied or saved.
   *
   * Not the arrangement the report ships with. The viewer's own is restored on every load, so
   * judging against the shipped one called a page changed before it had been touched.
   */
  private arrangementBaseline: string | null = null;
  /** While set, the baseline follows the page instead of being compared against it. */
  private isBaselinePending = true;
  /** The template needs the sentinel to tell the Default row from a saved one. */
  protected readonly defaultPresetOptionId = ProductionComparisonDashboardComponent.defaultPresetId;
  protected readonly presetPendingDeletionSig = signal<ReportGridPreset | null>(null);
  /** Set once the viewer's default has actually been put on the page; see onSettingsSnapshot. */
  private hasOpenedOnDefaultPreset = false;
  /**
   * Whether the viewer has changed anything themselves.
   *
   * Not the same as the page differing from its baseline: the tables record their sizes as they
   * load, so a page nobody has touched can still read as changed. Opening on the stored default
   * waits on this instead, because a cold load finishes its tables before the document arrives
   * and the difference was enough to skip the default altogether.
   */
  private hasUserTouchedArrangement = false;
  protected readonly presetEditorButtonsSig = computed(() =>
    this.buildPresetEditorButtons(this.hasArrangementChangesSig(), this.isSavingPresetSig(), !!this.activePresetSig()),
  );
  /**
   * Save and Save-as-new live in the picker itself rather than beside it.
   *
   * No `dropDown`: naming any button replaces the editor's own set, so leaving it out drops the
   * arrow, and the field opens the list when it is clicked anyway.
   *
   * Rebuilt rather than mutated: the editor reads this when the reference changes, so flipping
   * a flag in place would leave the buttons as they were.
   *
   * Both handlers re-enter Angular. These are the editor's own buttons rather than components,
   * so DevExtreme calls them outside the zone: the dialog opened, and nothing was drawn until
   * some unrelated event happened to run change detection — a wait of seconds, or of forever on
   * a page left alone.
   */
  private buildPresetEditorButtons(hasChanges: boolean, isSaving: boolean, hasLoadedPreset: boolean): unknown[] {
    const nothingToSave = !hasChanges;
    const disabled = nothingToSave || isSaving;
    const hint = (action: string): string =>
      isSaving ? 'Saving…' : nothingToSave ? 'Nothing has changed to save' : action;

    return [
      // Default is the page as it ships, and nothing can be written over it, so on Default there
      // is only the one thing to offer: keep what is on screen as a preset of its own.
      ...(hasLoadedPreset
        ? [
            {
              name: 'savePreset',
              location: 'after' as const,
              options: {
                icon: 'save',
                stylingMode: 'text' as const,
                disabled,
                hint: hint('Save the page over the selected preset'),
                onClick: (): void => this.ngZone.run(() => void this.savePresetInPlace()),
              },
            },
          ]
        : []),
      {
        name: 'addPreset',
        location: 'after' as const,
        options: {
          icon: 'plus',
          stylingMode: 'text' as const,
          disabled,
          hint: hint('Save the page as a new preset'),
          onClick: (): void => this.ngZone.run(() => this.openPresetDialog('new')),
        },
      },
    ];
  }
  @ViewChild('setupDialogRef') private readonly setupDialog?: ModalWindowComponent;
  @ViewChild('presetDialogRef') private readonly presetDialog?: ModalWindowComponent;
  @ViewChild('presetDeleteDialogRef') private readonly presetDeleteDialog?: ModalWindowComponent;
  /**
   * A write in flight, which the modal shows inside its own action button.
   *
   * The dialog used to close the moment it was clicked and leave the write to finish unwatched,
   * so a slow one looked exactly like a fast one until the picker changed by itself. It now
   * stays up, spinning, until the write lands.
   */
  protected readonly isSavingPresetSig = signal(false);
  protected readonly isDeletingPresetSig = signal(false);
  protected presetDraftName = '';
  /** Set when the dialog is renaming an existing preset rather than making one. */
  private presetDialogTarget: 'new' | 'rename' = 'new';
  /** Which preset a rename is for, since the row it came from need not be the loaded one. */
  private presetRenameTargetId: string | null = null;
  protected readonly hideableColumns = COMPARISON_HIDEABLE_COLUMNS;
  protected isColumnPickerVisible = false;

  /**
   * What each grid ships with, read back from the grid itself on every load.
   *
   * Deliberately never stored. A stored default is a copy of how the page looked on the day it
   * was written, so it goes stale the moment a column changes in code — and the only way to
   * unmask a changed default was to discard everyone's layout along with it. Read from the
   * live grid, it is always this build's default, and settings can outlive any column change.
   */
  private readonly pristineStates = new Map<string, ComparisonGridState>();
  /** What the viewer changed, per grid, and only that. This is what gets saved. */
  private readonly gridDiffs = new Map<string, ReportGridSettingsEntry>();
  /** One store per full-list table; see getAllEntitiesStore. */
  private readonly allEntitiesStores = new Map<string, CustomStore>();
  /** One store per mover table; see getMoverStore. */
  private readonly moverStores = new Map<string, CustomStore>();
  protected readonly DX_USD_CURRENCY_FORMAT = DX_USD_CURRENCY_FORMAT;

  protected readonly nameColumnMinWidth = COMPARISON_NAME_COLUMN_MIN_WIDTH;

  private settingsAgentDbId: string | null = null;
  /** Which view's settings are loaded, so a change of view starts again rather than merges. */
  private loadedSettingsScope: ReportingComparisonScope | null = null;
  private areSettingsLoaded = false;
  private resolveFirstSettings: (() => void) | null = null;
  /** One deferred state read per grid; see onGridStateChanged. */
  private readonly stateReadHandles = new Map<string, ReturnType<typeof setTimeout>>();
  /**
   * The highest document version this page has seen or written.
   *
   * A snapshot at or below it is this page's own write coming back, or something older that
   * was already superseded; either way there is nothing in it worth taking.
   */
  private lastKnownVersion = 0;
  /** Everything that depends on the viewer's settings waits on this rather than racing it. */
  private readonly settingsReady: Promise<void> = new Promise(resolve => {
    this.resolveFirstSettings = resolve;
  });

  /**
   * A grid's id within the report.
   *
   * No version, on purpose. The old keys carried one because a stored layout masked any
   * default changed afterwards, so the only fix was to rename the keys and lose everything
   * saved under the old ones. Only the difference from the default is stored now, so a column
   * change no longer needs anybody's settings thrown away.
   */
  protected getMoverGridId(
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
    direction: ComparisonTableDirection,
  ): string {
    return `mover.${datasetKey}.${level}.${direction}`;
  }

  protected getAllEntitiesGridId(block: BlockDatasetOptions, level: 'mga' | 'agent'): string {
    return `all.${this.toDatasetKey(block)}.${level}`;
  }

  protected getOverallGridId(datasetKey: ReportingComparisonDatasets): string {
    return `overall.${datasetKey}`;
  }

  /**
   * Reads the viewer's settings once, and puts them on any grid already waiting.
   *
   * Grids are built while this is in flight, so each one captures its own default and then
   * either applies the settings straight away or is caught up here.
   */
  private async resolveSettingsAgentDbId(): Promise<string | null> {
    if (this.settingsAgentDbId) {
      return this.settingsAgentDbId;
    }

    const agent = await firstValueFrom(this.authService.loggedInAgent$).catch(() => null);
    this.settingsAgentDbId = agent?.[BaseModelKeys.dbId] ?? null;

    return this.settingsAgentDbId;
  }

  /**
   * A change to the document, from this browser or another one.
   *
   * The viewer's own writes come back here too. Those are skipped while they are still in
   * flight, and the confirmed echo that follows carries what this page already holds, so
   * adopting it changes nothing. Somebody else's change is adopted and put on the grids, which
   * is the point: the same viewer with the page open twice sees one set of settings.
   */
  private onSettingsSnapshot(
    settings: ReportGridSettings | null,
    isLocalEcho: boolean,
    scope: ReportingComparisonScope,
  ): void {
    // A different view: the presets follow the document on their own, so only what the page
    // itself holds is put back.
    if (this.loadedSettingsScope !== scope) {
      this.loadedSettingsScope = scope;
      this.areSettingsLoaded = false;
      this.lastKnownVersion = 0;
      this.hasOpenedOnDefaultPreset = false;
      this.activePresetIdSig.set(ProductionComparisonDashboardComponent.defaultPresetId);
    }

    const hadLoaded = this.areSettingsLoaded;
    this.areSettingsLoaded = true;
    this.resolveFirstSettings?.();
    this.resolveFirstSettings = null;

    // Still on its way to the server, so it says what is already on screen.
    if (isLocalEcho && hadLoaded) {
      return;
    }

    const incomingVersion = Number(settings?.[ReportGridSettingsKeys.version] ?? 0);

    // The first snapshot is taken whatever it says: it is what the viewer left behind.
    if (hadLoaded && incomingVersion <= this.lastKnownVersion) {
      return;
    }

    this.lastKnownVersion = Math.max(this.lastKnownVersion, incomingVersion);

    if (!hadLoaded) {
      // Only the values are put in place: the page is about to ask for its data anyway, and the
      // grids read these as they are built.
      this.activePresetIdSig.set(ProductionComparisonDashboardComponent.defaultPresetId);
      this.putArrangementInPlace(this.buildShippedArrangement());
    }

    // The document arrives in more than one piece — an empty one held locally, then the stored
    // one — so the viewer's default cannot be looked for in the first snapshot alone. It waits
    // until a snapshot actually carries it, and only while the page is still untouched: someone
    // who has already started arranging it is not to have it taken from under them.
    const opening = this.presetsSig().find(preset => preset.id === this.defaultPresetIdSig());
    if (
      opening &&
      !this.hasOpenedOnDefaultPreset &&
      !this.hasUserTouchedArrangement &&
      this.activePresetIdSig() === ProductionComparisonDashboardComponent.defaultPresetId
    ) {
      this.hasOpenedOnDefaultPreset = true;
      this.activePresetIdSig.set(opening.id);
      this.applyArrangement(opening);
    } else if (hadLoaded && !this.presetsSig().some(preset => preset.id === this.activePresetIdSig())) {
      // Deleted in another tab; the arrangement stays on screen, it is just no longer named.
      this.activePresetIdSig.set(ProductionComparisonDashboardComponent.defaultPresetId);
    }

    this.resetArrangementBaseline();
    this.cdr.markForCheck();
  }

  /**
   * JSON with every object's keys in a fixed order.
   *
   * Firestore hands a map's keys back in its own order rather than the one they were written
   * in, so comparing plain `JSON.stringify` output says two identical documents differ — which
   * had this page treating the confirmation of its own write as somebody else's change, every
   * time, and putting the older document back over what the viewer had just done.
   */
  private toStableJson(value: unknown): string {
    return JSON.stringify(value, (_key, item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return item;
      }

      const source = item as Record<string, unknown>;

      return Object.keys(source)
        .sort()
        .reduce<Record<string, unknown>>((sorted, key) => {
          sorted[key] = source[key];

          return sorted;
        }, {});
    });
  }

  /**
   * Whether anything on the page differs from what the picker is naming.
   *
   * Against a preset this is a straight comparison, because a preset holds exactly what would
   * be stored now. Against Default it cannot be: the tables record their size as they load, so
   * an untouched page still carries an entry per table, and comparing against nothing at all
   * would call every page changed. The parts are asked instead, each by what already decides
   * whether it is worth storing.
   */
  private buildArrangementSignature(arrangement: ComparisonArrangement): string {
    return this.toStableJson({
      hiddenColumns: [...(arrangement.hiddenColumns ?? [])].sort(),
      grids: arrangement.grids ?? [],
      mgaTopCount: arrangement.mgaTopCount ?? null,
      agentTopCount: arrangement.agentTopCount ?? null,
      reportFilterValues: [...(arrangement.reportFilterValues ?? [])].sort(),
    });
  }

  /** How the page is arranged right now, in the form the baseline is held in. */
  private currentArrangementSignature(): string {
    return this.buildArrangementSignature({
      hiddenColumns: [...this.hiddenColumns],
      grids: this.buildGridEntries(),
      mgaTopCount: this.appliedSetup.mgaTopCount,
      agentTopCount: this.appliedSetup.agentTopCount,
      reportFilterValues: this.appliedReportFilterValues,
    });
  }

  /**
   * Whether the page has stopped moving of its own accord.
   *
   * Each table records its size once it has loaded, so the arrangement goes on changing for a
   * moment after the page appears without anybody having touched it. The baseline is taken
   * once that has stopped, so those do not read as the viewer's changes.
   */
  private isPageSettled(): boolean {
    const states = [...this.moverTableStates.values()];

    // A page with no tables yet has not settled, it has not started: taking the baseline then
    // froze it before the tables had recorded their sizes, and the page read as changed before
    // it had been touched.
    return (
      this.areSettingsLoaded &&
      states.length > 0 &&
      states.some(state => state.hasLoaded) &&
      states.every(state => !state.isLoading)
    );
  }

  /** Taken again whenever the page is handed a new arrangement, or has just stored one. */
  private resetArrangementBaseline(): void {
    this.isBaselinePending = true;
    this.refreshArrangementChanges();
  }

  /** Recomputed wherever something worth saving may have changed; see scheduleSave. */
  private refreshArrangementChanges(): void {
    const signature = this.currentArrangementSignature();

    if (this.isBaselinePending) {
      this.arrangementBaseline = signature;
      this.isBaselinePending = !this.isPageSettled();
    }

    // Both buttons unlock the moment anything differs from what the picker names, Default
    // included: a change made on Default is how the first preset gets made.
    this.hasArrangementChangesSig.set(signature !== this.arrangementBaseline);
  }

  /** Every grid that has something worth keeping, in a stable order so writes compare cleanly. */
  private buildGridEntries(): ReportGridSettingsEntry[] {
    return [...this.gridDiffs.entries()]
      .filter(([, entry]) => !isComparisonGridEntryEmpty(entry))
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([gridId, entry]) => ({ ...entry, id: gridId }));
  }

  /** The default this grid is judged against: what it ships with, under the page-wide choice. */
  private getEffectiveDefault(gridId: string): ComparisonGridState | null {
    const pristine = this.pristineStates.get(gridId);

    return pristine ? buildComparisonEffectiveDefault(pristine, this.hiddenColumns) : null;
  }

  /**
   * Puts the viewer's settings on a grid.
   *
   * Only when they would actually change it: this runs on content-ready, and handing a grid a
   * state it already holds would have it rebuild and report ready again without end.
   */
  private applyStoredSettings(gridId: string): void {
    const grid = this.gridsByGridId.get(gridId);
    const effective = this.getEffectiveDefault(gridId);
    if (!grid?.state || !effective || !this.areSettingsLoaded) {
      return;
    }

    const target = applyComparisonGridDiff(effective, this.gridDiffs.get(gridId));
    const current = grid.state() as ComparisonGridState;
    if (!isComparisonGridLayoutEmpty(buildComparisonGridDiff(current, target))) {
      grid.state(target);
    }

    this.cdr.markForCheck();
  }

  /**
   * Puts a grid back to how it ships: columns, widths, sort and filters.
   *
   * Which is now simply forgetting what the viewer changed, and handing back the default the
   * grid reported when it was built.
   */
  protected resetGridState(gridId: string): void {
    this.hasUserTouchedArrangement = true;
    const table = this.gridDiffs.get(gridId)?.table;
    if (table) {
      // A mover table's own settings are not the grid's to reset; resetMoverGrid does that.
      this.gridDiffs.set(gridId, { table });
    } else {
      this.gridDiffs.delete(gridId);
    }

    this.applyStoredSettings(gridId);
    this.markArrangementChanged();
  }

  /**
   * A mover table's reset, which is more than the grid's own.
   *
   * Clearing the grid gives back the declared columns and widths, but the sort and the page
   * size are not the grid's to restore: the order is served, and the opening sort is applied
   * here when the grid is built. So the table state goes back to its defaults too, and the
   * grid is put back in step with it.
   */
  protected async resetMoverGrid(
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
    direction: ComparisonTableDirection,
  ): Promise<void> {
    this.hasUserTouchedArrangement = true;
    const gridId = this.getMoverGridId(datasetKey, level, direction);
    this.gridDiffs.delete(gridId);
    this.applyStoredSettings(gridId);

    const state = this.getMoverTableState(datasetKey, level, direction);
    await state.resetToDefaults(this.getAppliedTopCount(level), () => this.cdr.markForCheck());

    this.gridsByGridId.get(gridId)?.columnOption?.(state.sortField, 'sortOrder', state.sortDescending ? 'desc' : 'asc');
    this.markArrangementChanged();
  }

  /**
   * Opens a grid's column picker.
   *
   * DevExtreme offers the chooser from a toolbar of its own, which would sit between the card
   * header and the table and carry a single button the header already has room for. That
   * toolbar is switched off and the picker is opened from here instead, so every table control
   * stays on one row.
   */
  protected openColumnChooser(gridId: string): void {
    this.gridsByGridId.get(gridId)?.showColumnChooser?.();
  }

  protected openColumnPicker(): void {
    this.isColumnPickerVisible = true;
  }

  protected isColumnShown(field: string): boolean {
    return !this.hiddenColumns.has(field);
  }

  protected get hiddenColumnCount(): number {
    return this.hiddenColumns.size;
  }

  protected onColumnVisibilityChanged(field: string, shown: boolean): void {
    // Every checkbox reports the value it was handed as it is built, which is not somebody
    // ticking one. Left alone it marked the page as touched before it had loaded — and put all
    // twelve grids through an apply they did not need.
    if (shown === this.isColumnShown(field)) {
      return;
    }

    this.hasUserTouchedArrangement = true;
    if (shown) {
      this.hiddenColumns.delete(field);
    } else {
      this.hiddenColumns.add(field);
    }

    this.commitColumnVisibility();
  }

  protected showAllColumns(): void {
    this.hasUserTouchedArrangement = true;
    if (!this.hiddenColumns.size) {
      return;
    }

    this.hiddenColumns.clear();
    this.commitColumnVisibility();
  }

  /**
   * The page-wide choice moves the default every grid is judged against, rather than being
   * written into each grid's own settings.
   *
   * Written into them it would mark all twelve grids as changed the moment one column was
   * hidden, and showing that column again later would leave twelve stored overrides still
   * hiding it.
   */
  private commitColumnVisibility(): void {
    for (const gridId of this.gridsByGridId.keys()) {
      this.applyStoredSettings(gridId);
      this.onGridStateChanged(gridId);
    }

    this.markArrangementChanged();
  }

  /** Every grid registers under its own id on creation; Reset and the pickers look it up there. */
  protected onGridStateHostInitialized(event: { component?: unknown }, gridId: string): void {
    const instance = event?.component as ComparisonGridHost | undefined;
    if (!instance) {
      return;
    }

    this.gridsByGridId.set(gridId, instance);
  }

  /**
   * The default is read here rather than on initialisation, because a grid is not finished
   * being built then: columns, widths and the opening sort are all applied afterwards, so a
   * state read at init describes a half-formed grid.
   */
  protected onGridStateHostReady(gridId: string): void {
    const instance = this.gridsByGridId.get(gridId);
    if (!instance?.state) {
      return;
    }

    if (!this.pristineStates.has(gridId)) {
      const state = instance.state() as ComparisonGridState | undefined;
      // A state describing no columns describes nothing; the grid will report ready again.
      if (!state?.columns?.length) {
        return;
      }

      this.pristineStates.set(gridId, state);
      this.applyStoredSettings(gridId);

      return;
    }

    this.onGridStateChanged(gridId);
  }

  /** A mover table's own settings, stored beside its grid's in the same document. */
  private persistMoverTableConfig(gridId: string, state: ComparisonTableState): void {
    const entry = this.gridDiffs.get(gridId) ?? {};
    this.gridDiffs.set(gridId, { ...entry, table: state.toPersistedConfig() });
    this.markArrangementChanged();
  }

  private readMoverTableConfig(gridId: string): ComparisonTablePersistedConfig | null {
    const table = this.gridDiffs.get(gridId)?.table;

    return typeof table?.pageSize === 'number' ? (table as ComparisonTablePersistedConfig) : null;
  }

  /**
   * How the page is arranged right now, as a record that can be come back to.
   *
   * No dates and no mode: a preset is an arrangement, not a period, so the same one can be put
   * on whatever range is on screen. The working arrangement goes on saving itself as the viewer
   * works; this is only written when they ask for it.
   */
  private capturePreset(id: string, name: string): ReportGridPreset {
    return {
      id,
      name: name.trim(),
      ...this.captureArrangement(),
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * How the page is arranged right now, saved or not: what a preset keeps, and what the PDF is
   * printed from — one capture, so the two can never disagree about what "the page" was.
   */
  private captureArrangement(): Required<ComparisonArrangement> {
    return {
      hiddenColumns: [...this.hiddenColumns],
      grids: this.buildGridEntries(),
      mgaTopCount: this.appliedSetup.mgaTopCount,
      agentTopCount: this.appliedSetup.agentTopCount,
      reportFilterValues: [...this.appliedReportFilterValues],
    };
  }

  /**
   * Puts a saved arrangement back on the page.
   *
   * The request at the end reloads every table anyway, so each mover's size and how far it had
   * been grown are put back first and picked up by that reload rather than causing one each.
   */
  /**
   * Driven by the row being clicked rather than by the value changing.
   *
   * Default is what the picker falls back to, so a page that has drifted from it is still
   * showing Default as selected — and a value that is already set never changes. Picking it
   * again is exactly how somebody asks for their changes to be dropped, so the click is what
   * this listens to.
   */
  protected onPresetSelected(presetId: string | null): void {
    this.hasUserTouchedArrangement = true;
    if (!presetId) {
      return;
    }

    if (presetId === ProductionComparisonDashboardComponent.defaultPresetId) {
      this.activePresetIdSig.set(ProductionComparisonDashboardComponent.defaultPresetId);
      this.applyArrangement({
        hiddenColumns: [],
        grids: [],
        mgaTopCount: ProductionComparisonDashboardComponent.defaultTopCount,
        agentTopCount: ProductionComparisonDashboardComponent.defaultTopCount,
        reportFilterValues: [],
      });

      return;
    }

    this.applyPreset(presetId);
  }

  protected applyPreset(presetId: string | null): void {
    const preset = this.presetsSig().find(entry => entry.id === presetId);
    if (!preset) {
      return;
    }

    this.activePresetIdSig.set(preset.id);
    this.applyArrangement(preset);
  }

  /**
   * Puts an arrangement on the page, whether it came from a preset or is the one it ships with.
   *
   * The request at the end reloads every table anyway, so each mover's size and how far it had
   * been grown are put back first and picked up by that reload rather than causing one each.
   */
  private buildShippedArrangement(): ComparisonArrangement {
    return {
      hiddenColumns: [],
      grids: [],
      mgaTopCount: ProductionComparisonDashboardComponent.defaultTopCount,
      agentTopCount: ProductionComparisonDashboardComponent.defaultTopCount,
      reportFilterValues: [],
    };
  }

  /** The values alone, for when the grids are not built yet and will read them as they are. */
  private putArrangementInPlace(arrangement: ComparisonArrangement): void {
    this.hiddenColumns.clear();
    for (const field of arrangement.hiddenColumns ?? []) {
      this.hiddenColumns.add(field);
    }

    this.gridDiffs.clear();
    for (const entry of arrangement.grids ?? []) {
      if (entry?.id) {
        this.gridDiffs.set(entry.id, entry);
      }
    }

    this.appliedSetup = {
      ...this.appliedSetup,
      mgaTopCount: arrangement.mgaTopCount ?? this.appliedSetup.mgaTopCount,
      agentTopCount: arrangement.agentTopCount ?? this.appliedSetup.agentTopCount,
    };
    this.appliedReportFilterValues = [...(arrangement.reportFilterValues ?? [])];
  }

  private applyArrangement(arrangement: ComparisonArrangement): void {
    this.putArrangementInPlace(arrangement);

    for (const gridId of this.gridsByGridId.keys()) {
      this.applyStoredSettings(gridId);
    }

    this.moverTableStates.forEach((state, key) => {
      const [datasetKey, level, direction] = key.split('|') as [
        ReportingComparisonDatasets,
        'mga' | 'agent',
        ComparisonTableDirection,
      ];
      const topCount = this.getAppliedTopCount(level);
      const stored = this.readMoverTableConfig(this.getMoverGridId(datasetKey, level, direction));

      state.restoreConfig(stored ?? { pageSize: topCount, loadedCount: topCount });
    });

    this.isBaselinePending = true;
    this.requestComparison();
    this.markArrangementChanged();
  }

  /** Save over the preset that is loaded; with none loaded there is nothing to save over. */
  /** Only offered with a preset loaded: Default has nothing to be saved over. */
  protected async savePresetInPlace(): Promise<void> {
    const active = this.activePresetSig();
    if (!active || this.isSavingPresetSig()) {
      return;
    }

    this.isSavingPresetSig.set(true);
    this.isBaselinePending = true;

    try {
      await this.writeSettings(
        this.presetsSig().map(preset => (preset.id === active.id ? this.capturePreset(active.id, active.name) : preset)),
        this.defaultPresetIdSig(),
      );
    } finally {
      this.isSavingPresetSig.set(false);
      this.cdr.markForCheck();
    }
  }

  /** Renaming from the row, which may not be the preset that is loaded. */
  protected renamePresetFromRow(presetId: string): void {
    const preset = this.presetsSig().find(entry => entry.id === presetId);
    if (!preset) {
      return;
    }

    this.presetRenameTargetId = preset.id;
    this.presetDialogTarget = 'rename';
    this.presetDraftName = preset.name;
    this.cdr.markForCheck();
    this.presetDialog?.showModal();
  }

  protected openPresetDialog(target: 'new' | 'rename'): void {
    this.presetRenameTargetId = null;
    this.presetDialogTarget = target;
    this.presetDraftName = target === 'rename' ? (this.activePresetSig()?.name ?? '') : '';
    this.cdr.markForCheck();
    this.presetDialog?.showModal();
  }

  protected get presetDialogTitle(): string {
    return this.presetDialogTarget === 'rename' ? 'Rename preset' : 'Save as new preset';
  }

  protected get canConfirmPresetDialog(): boolean {
    return this.presetDraftName.trim().length > 0;
  }

  protected async confirmPresetDialog(): Promise<void> {
    const name = this.presetDraftName.trim();
    if (!name || this.isSavingPresetSig()) {
      return;
    }

    let nextPresets: ReportGridPreset[];
    if (this.presetDialogTarget === 'rename') {
      const targetId = this.presetRenameTargetId ?? this.activePresetIdSig();
      nextPresets = this.presetsSig().map(preset =>
        preset.id === targetId ? { ...preset, name, updatedAt: new Date().toISOString() } : preset,
      );
    } else {
      const preset = this.capturePreset(this.createPresetId(), name);
      nextPresets = [...this.presetsSig(), preset];
      this.activePresetIdSig.set(preset.id);
    }

    this.isSavingPresetSig.set(true);
    this.isBaselinePending = true;

    try {
      await this.writeSettings(nextPresets, this.defaultPresetIdSig());
    } finally {
      this.isSavingPresetSig.set(false);
      this.presetDialog?.hideModal();
      this.cdr.markForCheck();
    }
  }

  /**
   * Deleting is asked about first.
   *
   * The icon sits in a list row that is otherwise clicked to load a preset, so it is an easy
   * thing to hit by accident — and what it throws away is an arrangement somebody built, with
   * nothing to bring it back.
   */
  protected requestPresetDeletion(presetId: string): void {
    const preset = this.presetsSig().find(entry => entry.id === presetId);
    if (!preset) {
      return;
    }

    this.presetPendingDeletionSig.set(preset);
    this.presetDeleteDialog?.showModal();
  }

  protected isDefaultPreset(presetId: string): boolean {
    return this.defaultPresetIdSig() === presetId;
  }

  /**
   * Which preset the report opens on.
   *
   * Pressed again it clears, and the report goes back to opening on what it ships with — there
   * is no separate way to say "none", and needing one for something this small would be worse.
   */
  protected toggleDefaultPreset(presetId: string): void {
    void this.writeSettings(this.presetsSig(), this.defaultPresetIdSig() === presetId ? null : presetId);
  }

  /** Also how the modal reports being dismissed, so a delete left mid-air clears itself. */
  protected cancelPresetDeletion(): void {
    if (this.isDeletingPresetSig()) {
      return;
    }

    this.presetPendingDeletionSig.set(null);
  }

  /** Deleting leaves the arrangement on screen; it is only no longer named. */
  protected async confirmPresetDeletion(): Promise<void> {
    const target = this.presetPendingDeletionSig();
    if (!target || this.isDeletingPresetSig()) {
      return;
    }

    // Only the preset being shown loses its name; deleting another leaves the page alone.
    if (this.activePresetIdSig() === target.id) {
      this.activePresetIdSig.set(ProductionComparisonDashboardComponent.defaultPresetId);
    }

    this.isDeletingPresetSig.set(true);
    this.isBaselinePending = true;

    try {
      await this.writeSettings(
        this.presetsSig().filter(preset => preset.id !== target.id),
        this.defaultPresetIdSig() === target.id ? null : this.defaultPresetIdSig(),
      );
    } finally {
      this.isDeletingPresetSig.set(false);
      this.presetPendingDeletionSig.set(null);
      this.presetDeleteDialog?.hideModal();
      this.cdr.markForCheck();
    }
  }

  private createPresetId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `preset-${Date.now().toString(36)}`;
  }

  /** Whether to offer Reset: only once the viewer has actually changed something. */
  protected isGridCustomised(gridId: string): boolean {
    return !isComparisonGridLayoutEmpty(this.gridDiffs.get(gridId));
  }

  /**
   * A mover table counts as changed if either half of it has been: the grid's layout, or its
   * own page size, row count and order.
   */
  protected isMoverCustomised(
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
    direction: ComparisonTableDirection,
  ): boolean {
    if (this.isGridCustomised(this.getMoverGridId(datasetKey, level, direction))) {
      return true;
    }

    const state = this.moverTableStates.get(this.buildMoverTableKey(datasetKey, level, direction));

    return !!state?.hasLoaded && !state.isDefaultConfig(this.getAppliedTopCount(level));
  }

  /**
   * Recomputed whenever a grid reports a change, so Reset appears the moment something is
   * altered and goes again when it is put back.
   */
  /**
   * A grid reporting that something about it changed.
   *
   * The read is put off by a turn rather than done here. DevExtreme announces the change before
   * it has applied it, so a state read in this turn still describes the order before the click
   * — the diff came out empty, nothing was stored, and the next snapshot put the grid back to
   * how it ships. Deferring also coalesces: one interaction reports several times.
   */
  protected onGridStateChanged(gridId: string): void {
    const pending = this.stateReadHandles.get(gridId);
    if (pending) {
      clearTimeout(pending);
    }

    this.stateReadHandles.set(
      gridId,
      setTimeout(() => {
        this.stateReadHandles.delete(gridId);
        this.readGridState(gridId);
      }),
    );
  }

  /** What the grid now holds, against what it ships with, stored as the difference. */
  private readGridState(gridId: string): void {
    const grid = this.gridsByGridId.get(gridId);
    const effective = this.getEffectiveDefault(gridId);
    if (!grid?.state || !effective || !this.areSettingsLoaded) {
      return;
    }

    const diff = buildComparisonGridDiff(grid.state() as ComparisonGridState, effective);
    const existing = this.gridDiffs.get(gridId);
    // The table half is not the grid's to report, so it is carried across untouched.
    if (existing?.table) {
      diff.table = existing.table;
    }

    if (JSON.stringify(existing ?? {}) === JSON.stringify(diff)) {
      return;
    }

    if (isComparisonGridEntryEmpty(diff)) {
      this.gridDiffs.delete(gridId);
    } else {
      this.gridDiffs.set(gridId, diff);
    }

    this.markArrangementChanged();
  }

  /**
   * Saving is deferred and coalesced: a resize drags through dozens of option changes, and all
   * twelve grids share one document.
   */
  /**
   * Something about the arrangement changed, and nothing is written.
   *
   * Nothing on this page saves itself. What the viewer has on screen is theirs until they put
   * it in a preset, which is the whole of what is stored — so all this does is work out whether
   * Save and + have anything left to do.
   */
  private markArrangementChanged(): void {
    this.refreshArrangementChanges();
    this.cdr.markForCheck();
  }

  /**
   * The presets, written because the viewer asked for one to be kept, renamed or dropped.
   *
   * Written and forgotten: the document reports the change back, and what is on screen is read
   * from that rather than set here.
   */
  private async writeSettings(presets: ReportGridPreset[], defaultPresetId: string | null): Promise<void> {
    const agentDbId = this.settingsAgentDbId;
    const scope = this.loadedSettingsScope;
    if (!agentDbId || !scope) {
      return;
    }

    this.refreshArrangementChanges();

    // An array, which a merging write replaces whole rather than walking into. That is what
    // lets a deleted preset actually go away.
    const settings = {
      [ReportGridSettingsKeys.reportId]: buildComparisonSettingsId(scope),
      [ReportGridSettingsKeys.presets]: presets,
      [ReportGridSettingsKeys.defaultPresetId]: defaultPresetId,
      // Claimed before the write goes out, so a snapshot of it arriving later is recognised as
      // ours whatever order it comes back in.
      [ReportGridSettingsKeys.version]: ++this.lastKnownVersion,
    } as ReportGridSettings;

    await this.gridSettingsService.save(agentDbId, settings).catch(() => null);
  }

  /** The shared width for a column, so the same column sits in the same place in every grid. */
  protected getColumnWidth(dataField: string): number | undefined {
    return COMPARISON_COLUMN_WIDTHS[dataField] ?? undefined;
  }

  /** One array per grid, rebuilt only when what names its columns moves; see getGridColumns. */
  private readonly gridColumnsCache = new Map<string, { key: string; columns: ComparisonGridColumnView[] }>();

  /**
   * A grid's columns, from the table catalog.
   *
   * Kept per grid and handed back as the same array until the scope or the period changes: the
   * template asks on every pass, and a new array each time would have every column re-bound.
   */
  protected getGridColumns(gridId: string): ComparisonGridColumnView[] {
    const scope = this.getScope();
    const ranges = this.getCaptionRanges();
    const key = [
      scope,
      ranges.currentRange.startDate,
      ranges.currentRange.endDate,
      ranges.comparisonRange.startDate,
      ranges.comparisonRange.endDate,
    ].join('|');
    const cached = this.gridColumnsCache.get(gridId);
    if (cached?.key === key) {
      return cached.columns;
    }

    const table = findComparisonTable(scope, gridId);
    const columns = table
      ? table.columns.map(column => this.toGridColumnView(table, column.field, column.kind, ranges))
      : [];
    this.gridColumnsCache.set(gridId, { key, columns });

    return columns;
  }

  /**
   * The options a column is declared with, which differ by the kind of table as much as by the
   * column: a mover's names are picked from a header filter and never sorted, its figures the
   * other way round; the full lists offer everything; the overall table offers neither and
   * opens unsorted. The name column is fixed to the left wherever the grid can scroll sideways.
   */
  private toGridColumnView(
    table: ComparisonTableDefinition,
    field: ComparisonColumnField,
    kind: ComparisonColumnKind,
    ranges: ComparisonRanges,
  ): ComparisonGridColumnView {
    const isName = field === 'entityName';
    const isText = kind === 'text';
    const isMover = table.kind === 'mover';
    const isFixed = isName && table.kind !== 'overall';
    const defaultSort = table.kind === 'all' && table.defaultSort?.field === field ? table.defaultSort : null;

    return {
      field,
      caption: getComparisonColumnCaption(field, table, ranges),
      width: this.getColumnWidth(field),
      minWidth: isName ? this.nameColumnMinWidth : undefined,
      dataType: isText ? undefined : 'number',
      format: kind === 'currency' ? DX_USD_CURRENCY_FORMAT : kind === 'decimal' ? COMPARISON_DECIMAL_FORMAT : undefined,
      customizeText: kind === 'percent' ? this.customizePercentCellText : undefined,
      selectedFilterOperation: !isText && table.kind !== 'overall' ? COMPARISON_DEFAULT_FILTER_OPERATION : undefined,
      sortOrder: defaultSort ? (defaultSort.desc ? 'desc' : 'asc') : undefined,
      allowHiding: !isName,
      allowSorting: !(isMover && isText),
      allowFiltering: !(isMover && isText),
      allowHeaderFiltering: isMover ? isText : true,
      fixed: isFixed,
      fixedPosition: isFixed ? 'left' : undefined,
    };
  }

  /** The workbook always; the PDF for the Alliance Group, MGA, and RMD views. */
  protected readonly exportMenuItemsSig = computed(() => {
    const scope = this.currentScopeSig();
    const offersPdf =
      scope === ReportingComparisonScopes.allianceGroup ||
      scope === ReportingComparisonScopes.mga ||
      scope === ReportingComparisonScopes.rmd;

    return [
      { id: 'excel', text: 'Excel', icon: 'xlsxfile' },
      ...(offersPdf ? [{ id: 'pdf', text: 'PDF', icon: 'pdffile' }] : []),
    ];
  });

  protected onExportMenuItemClick(event: { itemData?: { id?: string } }): void {
    if (event.itemData?.id === 'pdf') {
      this.openPdfPreview();

      return;
    }

    void this.exportDashboard();
  }

  /** Set while the PDF preview is open, with the page as it was when it opened. */
  protected readonly pdfPreviewSig = signal<ComparisonPdfPreviewState | null>(null);

  /**
   * Opens the PDF on the page as it is, saved or not.
   *
   * The arrangement is taken now and handed over whole, the shape a preset stores it in; the
   * reporting API replays it over the same catalog the grids were declared from. Only blocks with
   * something to print are offered — the tests the page itself uses to decide what to draw.
   */
  protected openPdfPreview(): void {
    const comparisonData = this.comparisonDataSig();
    if (!comparisonData || this.isLoadingSig()) {
      return;
    }

    const scope = this.getScope();
    const blocks = getComparisonPdfBlocks(scope);
    const blockGroups = [ReportingComparisonDatasets.paid, ReportingComparisonDatasets.submit]
      .map(dataset => ({
        title: getComparisonDatasetTitle(dataset),
        blocks: blocks
          .filter(block => block.dataset === dataset && isComparisonPdfBlockAvailable(block, comparisonData[dataset]))
          .map(block => ({ id: block.id, label: block.label })),
      }))
      .filter(group => group.blocks.length > 0);

    if (!blockGroups.length) {
      return;
    }

    const filters = toReportFilters(this.appliedReportFilterValues);
    const { hiddenColumns, grids, mgaTopCount, agentTopCount } = this.captureArrangement();
    const request: ComparisonPdfRequestBase = {
      scope,
      current_start_date: this.currentStartDate,
      current_end_date: this.currentEndDate,
      comparison_start_date: this.comparisonStartDate,
      comparison_end_date: this.comparisonEndDate,
      ...(filters.mga.length ? { mga: filters.mga } : {}),
      ...(filters.rmd.length ? { rmd: filters.rmd } : {}),
      arrangement: { hiddenColumns, grids, mgaTopCount, agentTopCount },
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
    };

    this.pdfPreviewSig.set({ request, blockGroups, fileName: this.buildExportFileName() });
  }

  protected closePdfPreview(): void {
    this.pdfPreviewSig.set(null);
  }

  protected chartDatasetKey: ChartDatasetKey = 'paid';
  protected currentStartDate = getStartOfYearDate();
  protected currentEndDate = getLastPassedFriday();
  protected comparisonStartDate = shiftIsoDateByYears(this.currentStartDate, -1);
  protected comparisonEndDate = shiftIsoDateByYears(this.currentEndDate, -1);
  protected isSetupModalVisible = false;
  protected readonly isApplyingSetupSig = signal(false);
  /**
   * Reset lives in the modal's own footer, on the left.
   *
   * `ngZone.run` for the same reason as the preset buttons: a toolbar item's `onClick` is a
   * widget option, so DevExtreme calls it outside Angular and nothing would be redrawn.
   */
  protected readonly setupDialogToolbarItems = [
    {
      widget: 'dxButton',
      location: 'before',
      toolbar: 'bottom',
      options: {
        text: 'RESET DEFAULTS',
        stylingMode: 'text',
        onClick: (): void => this.ngZone.run(() => this.resetSetupDraft()),
      },
    },
  ];
  protected appliedSetup: ComparisonSetupState = this.createDefaultSetup();
  protected draftSetup: ComparisonSetupState = this.createDefaultSetup();
  protected appliedReportFilterValues: string[] = [];
  protected readonly BlockDatasetOptions = BlockDatasetOptions;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly dashboardContext = inject(PRODUCTION_DASHBOARD_CONTEXT);
  private readonly comparisonExportService = inject(ProductionComparisonExportService);
  private readonly exportBuilder = inject(ProductionComparisonExportBuilderService);
  private readonly comparisonReportService = inject(ProductionComparisonReportService);
  private readonly comparisonRowsService = inject(ProductionComparisonRowsService);
  private readonly reportFilterOptionsService = inject(ReportFilterOptionsService);
  private readonly authService = inject(AuthService);
  private readonly gridSettingsService = inject(ReportGridSettingsService);
  /** Empty until the viewer's scope is known to be visible; see requestComparison. */
  private readonly query$ = new BehaviorSubject<ReportingComparisonHttpQuery | null>(null);
  /**
   * The as-of date the shell reports, wrapped so that not having answered yet (undefined) is told
   * apart from having answered that there is none ({ asOfDate: null }).
   *
   * The page settles its period on the first answer, and a signal's initial value is not one.
   * Settling on it opened every page through the last Friday, whatever the loaded paid file ran
   * to, and the real date — arriving a moment later from the upload history — was ignored.
   */
  private readonly paidFileAsOfReportSig = toSignal(
    this.dashboardContext.paidFileAsOfData$.pipe(map(asOfDate => ({ asOfDate }))),
    { initialValue: undefined },
  );
  private readonly paidFileAsOfDateSig = computed(() => this.paidFileAsOfReportSig()?.asOfDate ?? null);
  private readonly yearsSig = toSignal(this.dashboardContext.years$, { initialValue: [] as number[] });
  private hasInitializedSetupFromAsOfDate = false;
  /** The scope of the last comparison sent, so the scope effect doesn't repeat the startup request. */
  private lastRequestedScope: ReportingComparisonScope | null = null;
  private reportFilterReloadTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly currentScopeSig = toSignal(
    this.route.paramMap.pipe(
      map(params => parseReportScopeParam(params.get('scope'))),
      distinctUntilChanged(),
    ),
    { initialValue: null },
  );

  /**
   * The viewer's settings for the view they are looking at.
   *
   * Followed rather than read once. The route keeps this component when only the scope changes,
   * and each view has its own document: the grids are not the same between them — an MGA view
   * carries no MGA column, an RMD view is narrowed to their own agencies — so one document for
   * all three meant arranging the page at one level rearranged it at the others.
   */
  private readonly settingsSnapshotSig = toSignal(
    toObservable(this.currentScopeSig).pipe(
      switchMap(scope => {
        if (!scope) {
          return of(null);
        }

        return from(this.resolveSettingsAgentDbId()).pipe(
          switchMap(agentDbId =>
            agentDbId
              ? this.gridSettingsService
                  .watchByReportId(agentDbId, buildComparisonSettingsId(scope))
                  .pipe(map(emission => ({ scope, ...emission })))
              : of({ scope, settings: null, isLocalEcho: false }),
          ),
          catchError(() => of({ scope, settings: null, isLocalEcho: false })),
        );
      }),
    ),
    { initialValue: null },
  );

  protected readonly scopeLabelSig = computed(() => {
    const scope = this.currentScopeSig();
    return scope ? REPORT_SCOPE_LABELS[scope] : '';
  });

  protected readonly pageTitleSig = computed(() => `${this.scopeLabelSig()} Comparison Dashboard`);

  /** Other agencies' names stay off screen on scoped pages; the MGA page has nothing to filter. */
  protected readonly reportFilterOptionsSig = toSignal(
    toObservable(
      computed(() => ({ scope: this.currentScopeSig(), agent: this.dashboardContext.viewedAgentSig() })),
    ).pipe(
      // The agent is only a trigger: the API reads the viewed agent from the request.
      switchMap(({ scope }) => this.reportFilterOptionsService.optionsFor(scope, ReportFilterOptionsPages.comparison)),
    ),
    { initialValue: [] as ReportFilterOption[] },
  );

  protected readonly reportFilterPlaceholderSig = computed(() => getReportFilterPlaceholder(this.currentScopeSig()));

  protected readonly showReportFilterSig = computed(() => this.currentScopeSig() !== ReportingComparisonScopes.mga);

  protected readonly availableYearsSig = computed<number[]>(() => {
    const years = [...(this.yearsSig() ?? [])].filter(Boolean).sort((a, b) => b - a);
    const currentYear = new Date().getFullYear();
    const fallbackYears = Array.from({ length: 6 }, (_, index) => currentYear - index);
    const combined = years.length ? [...new Set([currentYear, ...years, ...fallbackYears])] : fallbackYears;

    return combined.sort((a, b) => b - a);
  });
  protected readonly currentYearOptionsSig = computed<SelectOption<number>[]>(() =>
    this.availableYearsSig().map(year => ({ id: year, text: `${year}` })),
  );
  protected readonly compareYearOptionsSig = this.currentYearOptionsSig;
  protected readonly validateDraftCurrentRangeOrder = (_event?: ValidationCallbackData): boolean =>
    this.isRangeOrdered(this.draftSetup.currentRangeStart, this.draftSetup.currentRangeEnd);
  protected readonly validateDraftComparisonRangeOrder = (_event?: ValidationCallbackData): boolean =>
    this.isRangeOrdered(this.draftSetup.comparisonRangeStart, this.draftSetup.comparisonRangeEnd);
  protected readonly validateDraftRangesDoNotOverlap = (_event?: ValidationCallbackData): boolean => {
    if (
      !this.isRangeOrdered(this.draftSetup.currentRangeStart, this.draftSetup.currentRangeEnd) ||
      !this.isRangeOrdered(this.draftSetup.comparisonRangeStart, this.draftSetup.comparisonRangeEnd)
    ) {
      return true;
    }

    return !this.rangesOverlap(
      this.draftSetup.currentRangeStart,
      this.draftSetup.currentRangeEnd,
      this.draftSetup.comparisonRangeStart,
      this.draftSetup.comparisonRangeEnd,
    );
  };

  protected readonly isLoadingSig = signal(false);
  /**
   * Resolves when the comparison in flight has answered, so whoever asked for it can wait —
   * see `applySetup`.
   *
   * A promise rather than watching the loading signal: `toObservable` replays what it emitted
   * at the last flush, so a wait started in the same turn as the request was handed the `false`
   * from before it and finished immediately, which is exactly the wait not happening.
   */
  private comparisonSettled: Promise<void> | null = null;
  private resolveComparisonSettled: (() => void) | null = null;
  protected isExporting = false;
  /**
   * Which card is on the whole screen, by its grid's id.
   *
   * Keyed by card rather than by dataset, which was ambiguous: the All MGAs and All Agents
   * cards of one dataset share it, so expanding either put both into the full-screen layout.
   */
  protected fullScreenCardId: string | null = null;
  protected loadErrorMessage: string | null = null;
  /** Set when the API refused the comparison: nothing to show, rather than a failure to retry. */
  protected unavailableReason: ReportUnavailableReason | null = null;
  /**
   * Whether this scope has answered once. The header's setup controls wait for it, so a refused
   * scope never flashes them; after that they stay, so a refused setup or filter can be changed back.
   */
  protected hasLoadedOnce = false;
  protected readonly comparisonDataSig = toSignal(
    this.query$.pipe(
      filter((query): query is ReportingComparisonHttpQuery => query !== null),
      switchMap(query => {
        this.isLoadingSig.set(true);
        this.loadErrorMessage = null;
        this.unavailableReason = null;

        return this.comparisonReportService.getComparisonReport(query).pipe(
          tap(() => (this.hasLoadedOnce = true)),
          catchError(error => {
            this.unavailableReason = getReportUnavailableReason(error);
            if (!this.unavailableReason) {
              this.hasLoadedOnce = true;
              console.error('[ProductionComparisonDashboard] Failed to load comparison report', { query, error });
              this.loadErrorMessage = this.getLoadErrorMessage(error);
            }
            return of(null);
          }),
          finalize(() => {
            this.isLoadingSig.set(false);
            this.resolveComparisonSettled?.();
            this.resolveComparisonSettled = null;
            this.cdr.markForCheck();
          }),
        );
      }),
    ),
    { initialValue: null },
  );

  constructor() {
    // The settings document for the view on screen, put on the page as it arrives and again
    // whenever the viewer moves to another view.
    effect(() => {
      const snapshot = this.settingsSnapshotSig();
      if (!snapshot) {
        return;
      }

      untracked(() => this.onSettingsSnapshot(snapshot.settings, snapshot.isLocalEcho, snapshot.scope));
    });

    discardLegacyLocalGridState();

    effect(() => {
      const comparisonData = this.comparisonDataSig();
      const availableChartOptions = this.getAvailableChartDatasetOptions(comparisonData);

      if (!availableChartOptions.length) {
        return;
      }

      if (!availableChartOptions.some(option => option.id === this.chartDatasetKey)) {
        this.chartDatasetKey = availableChartOptions[0].id;
      }
    });

    // The period the page opens on, from the as-of date the shell reports — once it has reported.
    // Read from the signal that already holds that stream rather than subscribed to a second time.
    // Every shell answers: a failed or forbidden summary comes back empty, and then the last
    // Friday stands in.
    effect(() => {
      const report = this.paidFileAsOfReportSig();

      untracked(() => {
        if (this.hasInitializedSetupFromAsOfDate || !report) {
          return;
        }

        const normalizedAsOfDate = normalizeAsOfDate(report.asOfDate) ?? getLastPassedFriday();
        const currentYear = Number(normalizedAsOfDate.slice(0, 4));
        const compareYear = currentYear - 1;
        const defaultQuarter = quarterFromIsoDate(normalizedAsOfDate);

        this.appliedSetup = {
          mode: 'ytd',
          currentYear,
          compareYear,
          stopDate: normalizedAsOfDate,
          currentRangeStart: `${currentYear}-01-01`,
          currentRangeEnd: normalizedAsOfDate,
          comparisonRangeStart: `${compareYear}-01-01`,
          comparisonRangeEnd: shiftIsoDateByYears(normalizedAsOfDate, -1),
          quarters: [defaultQuarter],
          mgaTopCount: ProductionComparisonDashboardComponent.defaultTopCount,
          agentTopCount: ProductionComparisonDashboardComponent.defaultTopCount,
        };
        this.draftSetup = { ...this.appliedSetup, quarters: [...this.appliedSetup.quarters] };
        this.applyDerivedRanges(this.appliedSetup);
        this.requestComparison();
        this.hasInitializedSetupFromAsOfDate = true;
        this.cdr.markForCheck();
      });
    });

    // Loads when the scope changes or becomes visible (the permission map can arrive late). The
    // first load waits for the as-of date above, which sends it, so opening the page asks once.
    effect(() => {
      const scope = this.currentScopeSig();
      const isScopeVisible = this.isScopeVisibleSig();
      untracked(() => {
        if (!isScopeVisible || !this.hasInitializedSetupFromAsOfDate || scope === this.lastRequestedScope) {
          return;
        }

        this.chartDatasetKey = 'paid';
        this.hasLoadedOnce = false;
        this.requestComparison();
      });
    });
  }

  protected openSetupModal(): void {
    this.draftSetup = {
      ...this.appliedSetup,
      quarters: [...this.appliedSetup.quarters],
    };
    this.isSetupModalVisible = true;
    this.cdr.markForCheck();
    this.setupDialog?.showModal();
  }

  /** However it was dismissed — Cancel, the close button, Escape — the header stops previewing. */
  protected onSetupModalHidden(): void {
    this.isSetupModalVisible = false;
    this.cdr.markForCheck();
  }

  /**
   * Stays open, with the button spinning, until the comparison it asked for has answered.
   *
   * Applying used to close the modal the moment it was clicked, which said the setup had been
   * taken but nothing about whether the page behind had caught up — on a slow range the old
   * figures sat there looking like the answer. Waiting also stops a second Apply going out
   * while the first is still in flight.
   */
  protected async applySetup(): Promise<void> {
    this.hasUserTouchedArrangement = true;
    const validationResult = this.setupFormComponent?.instance.validate();
    if (validationResult && !validationResult.isValid) {
      return;
    }

    if (this.isApplyingSetupSig()) {
      return;
    }

    this.appliedSetup = {
      ...this.draftSetup,
      quarters: [...this.draftSetup.quarters],
    };
    this.applyDerivedRanges(this.appliedSetup);

    this.isApplyingSetupSig.set(true);

    try {
      this.requestComparison();
      await this.comparisonSettled;
    } finally {
      this.isApplyingSetupSig.set(false);
      this.setupDialog?.hideModal();
      this.cdr.markForCheck();
    }
  }

  protected resetSetupDraft(): void {
    const fallbackStopDate = normalizeAsOfDate(this.paidFileAsOfDateSig()) ?? getLastPassedFriday();
    const currentYear = Number(fallbackStopDate.slice(0, 4));
    this.draftSetup = {
      mode: 'ytd',
      currentYear,
      compareYear: currentYear - 1,
      stopDate: fallbackStopDate,
      currentRangeStart: `${currentYear}-01-01`,
      currentRangeEnd: fallbackStopDate,
      comparisonRangeStart: `${currentYear - 1}-01-01`,
      comparisonRangeEnd: shiftIsoDateByYears(fallbackStopDate, -1),
      quarters: [quarterFromIsoDate(fallbackStopDate)],
      mgaTopCount: ProductionComparisonDashboardComponent.defaultTopCount,
      agentTopCount: ProductionComparisonDashboardComponent.defaultTopCount,
    };
  }

  /**
   * The header picker applies itself, as it does on the bulletin pages: Setup is for the date
   * range. Debounced, so picking several agencies asks the API once.
   */
  protected onReportFilterValueChanged(value: string[]): void {
    // The picker reports its value as it is built, which is not somebody choosing one: taking
    // that for a change marked the page as touched before it had finished loading, and the
    // stored default is only put on a page nobody has touched.
    const isSameValue =
      JSON.stringify([...value].sort()) === JSON.stringify([...this.appliedReportFilterValues].sort());
    if (!isSameValue) {
      this.hasUserTouchedArrangement = true;
    }

    this.appliedReportFilterValues = value;

    if (this.reportFilterReloadTimer) {
      clearTimeout(this.reportFilterReloadTimer);
    }

    this.reportFilterReloadTimer = setTimeout(() => {
      this.reportFilterReloadTimer = null;
      this.requestComparison();
      this.cdr.markForCheck();
    }, 1000);
  }

  protected goToMyReports(): void {
    void this.router.navigate(this.dashboardContext.myReportsRouteCommands);
  }

  protected retryLoad(): void {
    this.requestComparison();
  }

  protected toggleFullScreen = async (container: HTMLElement): Promise<void> => {
    if (this.document.fullscreenElement) {
      await this.document.exitFullscreen();

      return;
    }

    this.document
      .querySelectorAll('.ag-full-screen-app')
      .forEach(element => element.classList.remove('ag-full-screen-app'));
    await this.document?.documentElement?.requestFullscreen();
    container.classList.add('ag-full-screen-app');
  };

  /**
   * Full screen left by any route — Escape, F11, the browser's own control — puts the page back.
   *
   * The listener had ended up on the method below this one, so nothing did: a card left full
   * screen by Escape kept the layout, and its grid went on asking for the whole viewport's
   * height inside a card that no longer had it.
   */
  @HostListener('document:fullscreenchange')
  protected onFullscreenChange(): void {
    if (this.document.fullscreenElement) {
      return;
    }

    this.clearFullScreenCard();
  }

  protected showAllMgasProductionTable(): boolean {
    return this.currentScopeSig() === ReportingComparisonScopes.allianceGroup;
  }

  /** Both agent-backed pages list their people: an MGA's own agents, an RMD's across their agencies. */
  protected showAllAgentsProductionTable(): boolean {
    return (
      this.currentScopeSig() === ReportingComparisonScopes.mga ||
      this.currentScopeSig() === ReportingComparisonScopes.rmd
    );
  }

  /**
   * The same sizes the pager offers, and the size it opens at.
   *
   * Bound rather than repeated in the template: both grids carried their own copy of the list
   * and of the opening size, so a change here left the pagers offering something else.
   */
  protected readonly allTablePageSizes: number[] = [...COMPARISON_ALL_TABLE_PAGE_SIZES];
  protected readonly allTableDefaultPageSize = COMPARISON_ALL_TABLE_DEFAULT_PAGE_SIZE;

  /**
   * The full-list tables page against the same endpoint as the movers.
   *
   * They are the ones that most needed it: a company-wide agent list is 7,929 rows, which the
   * page payload could only have carried by sending all of them.
   */
  /**
   * The full-list tables are a DevExtreme store rather than an array of loaded rows, so the
   * grid's own pager, filter row and header filter act on the whole set. Filtering a loaded
   * page would narrow what is on screen and quietly ignore everything behind it.
   *
   * One store per table, kept so the grid is not handed a new data source on every change
   * detection pass.
   */
  protected getAllEntitiesStore(block: BlockDatasetOptions, level: 'mga' | 'agent'): CustomStore {
    const datasetKey = this.toDatasetKey(block);
    const key = `${datasetKey}|${level}`;
    const existing = this.allEntitiesStores.get(key);
    if (existing) {
      return existing;
    }

    const store = createComparisonRowsStore(this.comparisonRowsService, () => this.buildRowsBody(datasetKey, level));
    this.allEntitiesStores.set(key, store);

    return store;
  }

  /**
   * Every row a full-list table holds, for the export — not the page on screen, and not
   * narrowed by a filter the viewer happens to have typed.
   */
  private async fetchAllEntitiesRows(
    block: BlockDatasetOptions,
    level: 'mga' | 'agent',
  ): Promise<ReportingComparisonRowDto[]> {
    // Narrowed the way the table on screen is. It used to ask for the set regardless, so a
    // viewer who had filtered a table down to one agency still got all hundred and thirty back
    // — every row of something they were not looking at.
    const grid = this.gridsByGridId.get(this.getAllEntitiesGridId(block, level)) as
      | { getCombinedFilter?: (force: boolean) => unknown }
      | undefined;
    const filter = grid?.getCombinedFilter?.(true) ?? null;

    const response = await this.comparisonRowsService.getRows({
      ...this.buildRowsBody(this.toDatasetKey(block), level),
      loadOptions: {
        sort: [{ selector: 'premiumDifference', desc: true }],
        ...(filter ? { filter } : {}),
        skip: 0,
      },
    });

    return response.data;
  }

  private toDatasetKey(block: BlockDatasetOptions): ReportingComparisonDatasets {
    return block === BlockDatasetOptions.submit ? ReportingComparisonDatasets.submit : ReportingComparisonDatasets.paid;
  }

  protected isCardFullScreen(cardId: string): boolean {
    return this.fullScreenCardId === cardId;
  }

  /**
   * Full height in full screen, and its content's height otherwise.
   *
   * It used to be pinned to ten rows' worth and scroll inside itself, which made a page of
   * twenty-five rows read as ten with a hidden remainder — two scrollbars for one list. The
   * pager bounds the height now, so the table can simply be as tall as its page.
   */
  protected getCardGridHeight(cardId: string): number | string {
    return this.isCardFullScreen(cardId) ? '100%' : 'auto';
  }

  /**
   * Which card owns the screen is this page's own state, and native full screen is asked for on
   * top of it — not the other way round. Keying the way back on `document.fullscreenElement`
   * left no way back at all wherever the request had been refused: an embedded browser, a frame
   * without the permission, a gesture the await had already spent. The card expanded and the
   * button that should have closed it re-expanded the same card.
   */
  protected async toggleCardFullScreen(cardId: string, container: HTMLElement): Promise<void> {
    if (this.isCardFullScreen(cardId)) {
      await this.exitCardFullScreen();

      return;
    }

    this.fullScreenCardId = cardId;

    try {
      await this.toggleFullScreen(container);
    } finally {
      this.cdr.detectChanges();
      this.reserveRoomForOverlappingChrome(container);
    }
  }

  /** The layout goes back first, so it goes back whether or not there is a full screen to leave. */
  private async exitCardFullScreen(): Promise<void> {
    this.clearFullScreenCard();

    if (this.document.fullscreenElement) {
      await this.document.exitFullscreen().catch(() => undefined);
    }
  }

  private clearFullScreenCard(): void {
    this.fullScreenCardId = null;
    this.document.querySelectorAll('.ag-full-screen-app').forEach(element => {
      element.classList.remove('ag-full-screen-app');
      (element as HTMLElement).style?.removeProperty(ProductionComparisonDashboardComponent.fullScreenTopProperty);
    });
    this.cdr.markForCheck();
  }

  /**
   * How far down the screen an expanded card has to start.
   *
   * It stacks below DevExtreme's popups, so the grid's own dialogs still win — which also means
   * an app header above that line paints over the card's top. Rather than carrying one app's
   * header height as a number, the card is laid out flush and then asked what is actually in
   * front of it: ARM's header is a flex item at z-index 1501 and keeps its room, while the
   * portal's is an ordinary element the card simply covers. An app that grows a second bar
   * needs nothing here.
   */
  private reserveRoomForOverlappingChrome(card: HTMLElement): void {
    card.style.removeProperty(ProductionComparisonDashboardComponent.fullScreenTopProperty);

    const probes = [0.25, 0.5, 0.75].map(fraction => Math.round(window.innerWidth * fraction));
    let offset = 0;

    for (const x of probes) {
      const inFront = this.document.elementsFromPoint(x, 2)[0] as HTMLElement | undefined;
      if (!inFront || inFront === card || card.contains(inFront)) {
        continue;
      }

      offset = Math.max(offset, inFront.getBoundingClientRect().bottom);
    }

    if (offset > 0) {
      card.style.setProperty(
        ProductionComparisonDashboardComponent.fullScreenTopProperty,
        `${Math.round(offset)}px`,
      );
    }
  }

  protected async exportAllMgasTable(
    dataset: ReportingComparisonDatasetDto,
    block: BlockDatasetOptions,
  ): Promise<void> {
    await this.exportAllEntitiesTable({
      rows: await this.fetchAllEntitiesRows(block, 'mga'),
      table: getComparisonAllTable(this.getScope(), this.toDatasetKey(block), 'mga'),
      sheetName: 'All MGAs Production',
      fileName: this.buildAllMgasExportFileName(block),
    });
  }

  protected async exportAllAgentsTable(
    dataset: ReportingComparisonDatasetDto,
    block: BlockDatasetOptions,
  ): Promise<void> {
    await this.exportAllEntitiesTable({
      rows: await this.fetchAllEntitiesRows(block, 'agent'),
      table: getComparisonAllTable(this.getScope(), this.toDatasetKey(block), 'agent'),
      sheetName: 'All Agents Production',
      fileName: this.buildAllAgentsExportFileName(block),
    });
  }

  private async exportAllEntitiesTable(args: {
    rows: ReportingComparisonRowDto[];
    table: ComparisonTableDefinition;
    sheetName: string;
    fileName: string;
  }): Promise<void> {
    const rows = [...args.rows].sort((left, right) => right.premiumDifference - left.premiumDifference);
    if (!rows.length || this.isExporting) {
      return;
    }

    this.isExporting = true;
    this.cdr.markForCheck();

    try {
      await this.comparisonExportService.exportTable({
        fileName: args.fileName,
        sheetName: args.sheetName,
        columns: this.exportBuilder.buildTableColumns(this.exportContext, args.table),
        rows,
      });
    } finally {
      this.isExporting = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * One mover pair, both directions, as the export wants it.
   *
   * Written once rather than per dataset and level: the four pairs differ only in which rows
   * they read, and the catalog already knows which columns each carries.
   */
  private buildMoverExportPair(
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
  ): ProductionComparisonExportTablePair {
    const table = (direction: ComparisonTableDirection) => ({
      title: this.getMoverTableTitle(datasetKey, level, direction, this.getMoverTableState(datasetKey, level, direction)),
      columns: this.exportBuilder.buildTableColumns(
        this.exportContext,
        getComparisonMoverTable(this.getScope(), datasetKey, level, direction),
      ),
      rows: this.getMoverRows(direction, level, datasetKey),
    });

    return { increaseTable: table('increase'), decreaseTable: table('decrease') };
  }

  /**
   * A dataset's sheet: the cards, the charts, the overall row and the movers.
   *
   * Paid and Submitted were written out separately and were the same sheet twice over. They
   * differ in what they are called, whether goals come with them, and which rows they read.
   */
  private buildDatasetExportSheet(
    block: BlockDatasetOptions,
    dataset: ReportingComparisonDatasetDto,
    groupByLabel: string,
  ): ProductionComparisonExportSheet {
    const isPaid = block === BlockDatasetOptions.paid;
    const datasetKey = this.toDatasetKey(block);
    const label = isPaid ? 'Paid' : 'Submitted';

    return {
      sheetName: `${label} Production`,
      title: this.pageTitleSig(),
      subtitle: `${label} production export`,
      sections: [
        {
          title: `${label} Production`,
          description: isPaid
            ? 'Side-by-side paid totals, movers, and trend comparison.'
            : 'Submitted production for direct range-over-range comparison without goal-based scoring.',
          cards: this.getOverviewCards(dataset, isPaid),
          charts: { trend: dataset.trend, ranking: dataset.ranking, ranges: this.getCaptionRanges() },
          overallTable: {
            title: 'Overall comparison',
            // The overall row is an entity rather than an agency, whatever the grid calls it.
            columns: this.exportBuilder.buildTableColumns(
              this.exportContext,
              getComparisonOverallTable(this.getScope(), datasetKey),
              'Entity',
            ),
            rows: this.getOverallComparisonRows(dataset),
          },
          moverGridPairs: [
            ...(this.showEntityMoverGrids() ? [this.buildMoverExportPair(datasetKey, 'mga')] : []),
            ...(this.hasAgentMoverGrids(dataset) ? [this.buildMoverExportPair(datasetKey, 'agent')] : []),
          ],
        },
      ],
    };
  }

  protected async exportDashboard(): Promise<void> {
    const comparisonData = this.comparisonDataSig();
    if (!comparisonData || this.isExporting) {
      return;
    }

    this.isExporting = true;
    this.cdr.markForCheck();

    try {
      // An RMD page has no All MGAs list: its MGA-level rows group by RMD and agency, so the sheet
      // named every RMD against each agency — the same agency twice, once per RMD on its rows.
      const includeAllMgasSheets = this.getScope() !== ReportingComparisonScopes.rmd;

      // Fetched before the workbook is built: each is the table's whole set rather than the
      // page of it on screen, and both are asked for at once rather than one after the other.
      const [allPaidMgaRows, allSubmittedMgaRows] = includeAllMgasSheets
        ? await Promise.all([
            this.fetchAllEntitiesRows(BlockDatasetOptions.paid, 'mga'),
            this.fetchAllEntitiesRows(BlockDatasetOptions.submit, 'mga'),
          ])
        : [[], []];

      await this.comparisonExportService.exportWorkbook({
        fileName: this.buildExportFileName(),
        // Each dataset followed by its own full list, rather than both lists at the end: a
        // workbook is read a dataset at a time.
        sheets: [
          this.buildDatasetExportSheet(BlockDatasetOptions.paid, comparisonData.paid, comparisonData.groupByLabel),
          ...(includeAllMgasSheets
            ? [
                this.exportBuilder.buildAllMgasSheet(
                  this.exportContext,
                  getComparisonAllTable(this.getScope(), ReportingComparisonDatasets.paid, 'mga'),
                  allPaidMgaRows,
                ),
              ]
            : []),
          this.buildDatasetExportSheet(BlockDatasetOptions.submit, comparisonData.submit, comparisonData.groupByLabel),
          ...(includeAllMgasSheets
            ? [
                this.exportBuilder.buildAllMgasSheet(
                  this.exportContext,
                  getComparisonAllTable(this.getScope(), ReportingComparisonDatasets.submit, 'mga'),
                  allSubmittedMgaRows,
                ),
              ]
            : []),
        ],
      });
    } finally {
      this.isExporting = false;
      this.cdr.markForCheck();
    }
  }

  protected onDraftModeChange(event: { value: ComparisonPresetMode }): void {
    if (event.value === 'ranges' && this.draftSetup.mode !== 'ranges') {
      const derivedRanges = this.getDerivedRanges(this.draftSetup);
      this.draftSetup.currentRangeStart = derivedRanges.currentStartDate;
      this.draftSetup.currentRangeEnd = derivedRanges.currentEndDate;
      this.draftSetup.comparisonRangeStart = derivedRanges.comparisonStartDate;
      this.draftSetup.comparisonRangeEnd = derivedRanges.comparisonEndDate;
    }

    this.draftSetup.mode = event.value;
  }

  protected onDraftCurrentYearChange(event: { value: number }): void {
    this.draftSetup.currentYear = event.value;

    if (this.draftSetup.mode === 'ytd') {
      const { month, day } = toDateParts(this.draftSetup.stopDate);
      this.draftSetup.stopDate = this.clampStopDateToCurrentYear(buildIsoDate(event.value, month, day));
    }
  }

  protected onDraftCompareYearChange(event: { value: number }): void {
    this.draftSetup.compareYear = event.value;
  }

  protected onDraftStopDateChange(event: ValueChangedEvent): void {
    const nextValue = this.toInputDate(event.value, this.draftSetup.stopDate);
    this.draftSetup.stopDate = this.clampStopDateToCurrentYear(nextValue);
  }

  protected onDraftCurrentRangeStartInput(value: DateRangeBoxValue): void {
    this.draftSetup.currentRangeStart = this.toInputDate(value, this.draftSetup.currentRangeStart);
  }

  protected onDraftCurrentRangeEndInput(value: DateRangeBoxValue): void {
    this.draftSetup.currentRangeEnd = this.toInputDate(value, this.draftSetup.currentRangeEnd);
  }

  protected onDraftComparisonRangeStartInput(value: DateRangeBoxValue): void {
    this.draftSetup.comparisonRangeStart = this.toInputDate(value, this.draftSetup.comparisonRangeStart);
  }

  protected onDraftComparisonRangeEndInput(value: DateRangeBoxValue): void {
    this.draftSetup.comparisonRangeEnd = this.toInputDate(value, this.draftSetup.comparisonRangeEnd);
  }

  protected getDraftStopDateMin(): string {
    return `${this.draftSetup.currentYear}-01-01`;
  }

  protected getDraftStopDateMax(): string {
    return getEndOfYearDate(this.draftSetup.currentYear);
  }

  private getLoadErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Comparison data could not be loaded. Try again in a moment.';
  }

  protected getDisplayedSetupSummary(): string {
    return this.getSetupSummary(this.isSetupModalVisible ? this.draftSetup : this.appliedSetup);
  }

  /**
   * Cached per payload, because the result is bound straight to a grid's dataSource.
   *
   * The single-row case builds a new array, and a template binding is re-evaluated on every
   * change detection pass — so the grid was handed a different array each time and reloaded on
   * each one. Left alone that merely churned; once anything asked for another pass from the
   * grid's own contentReady it became an endless reload with the load panel never clearing.
   *
   * Keyed by the dataset object, so a new comparison naturally produces a new array.
   */
  private readonly overallComparisonRowsCache = new WeakMap<
    ReportingComparisonDatasetDto,
    ReportingComparisonRowDto[]
  >();

  protected getOverallComparisonRows(dataset: ReportingComparisonDatasetDto): ReportingComparisonRowDto[] {
    const cached = this.overallComparisonRowsCache.get(dataset);
    if (cached) {
      return cached;
    }

    const rows = dataset.overallRows?.length ? dataset.overallRows : [dataset.overallRow];
    this.overallComparisonRowsCache.set(dataset, rows);

    return rows;
  }

  protected hasOverallComparisonSummary(dataset: ReportingComparisonDatasetDto): boolean {
    return !!dataset.overallRows?.length;
  }

  protected getOverallComparisonSummaryCalculator(dataset: ReportingComparisonDatasetDto) {
    return (options: { name?: string; summaryProcess?: string; totalValue?: unknown }) => {
      if (options.summaryProcess !== 'finalize') {
        return;
      }

      switch (options.name) {
        case 'entityLabel':
          options.totalValue = 'Totals';
          break;
        case 'goalAmount':
          options.totalValue = dataset.overallRow.goalAmount;
          break;
        case 'goalAttainedPercent':
          options.totalValue = dataset.overallRow.goalAttainedPercent;
          break;
        case 'currentPremium':
          options.totalValue = dataset.overallRow.currentPremium;
          break;
        case 'currentPolicies':
          options.totalValue = dataset.overallRow.currentPolicies;
          break;
        case 'comparisonPremium':
          options.totalValue = dataset.overallRow.comparisonPremium;
          break;
        case 'comparisonPolicies':
          options.totalValue = dataset.overallRow.comparisonPolicies;
          break;
        case 'premiumDifference':
          options.totalValue = dataset.overallRow.premiumDifference;
          break;
        case 'policyDifference':
          options.totalValue = dataset.overallRow.policyDifference;
          break;
        case 'premiumChangePercent':
          options.totalValue = dataset.overallRow.premiumChangePercent;
          break;
        case 'policyChangePercent':
          options.totalValue = dataset.overallRow.policyChangePercent;
          break;
        default:
          break;
      }
    };
  }

  /** Company-wide and RMD pages group their own rows by MGA and by RMD, so agents get their own grids. */
  protected hasAgentMoverGrids(dataset: ReportingComparisonDatasetDto): boolean {
    return (
      hasComparisonMoverTables(this.getScope(), 'agent') &&
      // Deliberately the payload's movers, not the tables' own rows: a table has none until it
      // has rendered and asked for them, so judging by those would keep it from ever rendering.
      hasComparisonAgentMovers(dataset)
    );
  }

  /**
   * Whether the page's own rows are worth a mover grid. An RMD's are grouped by RMD — the viewer
   * is one — so they say nothing; the agent grids carry that page instead.
   */
  protected showEntityMoverGrids(): boolean {
    return hasComparisonMoverTables(this.getScope(), 'mga');
  }

  /**
   * The rows a mover table is showing: its own, always.
   *
   * It used to fall back to the movers that came with the page until the table had fetched
   * once, which meant the first rows and every row after them followed different rules — the
   * page's are filtered by sign before being cut, the table's are not — and left the table
   * unable to say how many rows it had, because the payload does not carry that count for
   * every level.
   */
  protected getMoverRows(
    direction: ComparisonTableDirection,
    level: 'mga' | 'agent',
    datasetKey: ReportingComparisonDatasets,
  ): ReportingComparisonRowDto[] {
    return this.getMoverTableState(datasetKey, level, direction).rows;
  }

  /**
   * A mover table's store, created on first use and kept, since the binding is read on every
   * change detection and a new store each time would reload the grid without end.
   */
  protected getMoverStore(
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
    direction: ComparisonTableDirection,
  ): CustomStore {
    const key = this.buildMoverTableKey(datasetKey, level, direction);
    const existing = this.moverStores.get(key);
    if (existing) {
      return existing;
    }

    const store = createComparisonMoverStore(this.getMoverTableState(datasetKey, level, direction));
    this.moverStores.set(key, store);

    return store;
  }

  /** Created on first use: which tables a scope shows is decided by the template. */
  protected getMoverTableState(
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
    direction: ComparisonTableDirection,
  ): ComparisonTableState {
    const key = this.buildMoverTableKey(datasetKey, level, direction);
    const existing = this.moverTableStates.get(key);
    if (existing) {
      return existing;
    }

    const gridId = this.getMoverGridId(datasetKey, level, direction);
    const state = new ComparisonTableState(
      this.comparisonRowsService,
      () => this.buildRowsBody(datasetKey, level),
      direction,
      this.getAppliedTopCount(level),
      isLoading => this.setMoverGridLoading(key, isLoading),
      () => this.persistMoverTableConfig(gridId, state),
    );

    this.moverTableStates.set(key, state);

    // The first load waits for the viewer's settings, so a table that had been grown or
    // reordered opens that way rather than fetching a default page and fetching again. It also
    // keeps the request out of the current turn: this runs while the template is being
    // rendered, and starting one here would change state the same pass is still reading.
    void this.settingsReady.then(() => {
      const restored = this.readMoverTableConfig(gridId);
      if (restored) {
        state.restoreConfig(restored);
      }

      return state.reload(() => this.cdr.markForCheck());
    });

    return state;
  }

  /** The grid registers itself so its load panel can be driven from the table's state. */
  protected onMoverGridInitialized(
    event: { component?: unknown },
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
    direction: ComparisonTableDirection,
  ): void {
    const key = this.buildMoverTableKey(datasetKey, level, direction);
    this.onGridStateHostInitialized(event, this.getMoverGridId(datasetKey, level, direction));

    const instance = event?.component as
      | { beginCustomLoading: (text: string) => void; endCustomLoading: () => void }
      | undefined;

    if (!instance) {
      return;
    }

    this.moverGrids.set(key, instance);

    // The grid opens on the order the table is already loading in. Applied here rather than
    // bound on the column: a binding re-asserts itself every change detection and would undo
    // the viewer's next header click.
    const state = this.moverTableStates.get(key);
    const sortable = instance as unknown as {
      columnOption?: (field: string, option: string, value: unknown) => void;
    };
    if (state) {
      sortable.columnOption?.(state.sortField, 'sortOrder', state.sortDescending ? 'desc' : 'asc');
    }

    // A page can already be in flight: the opening load starts before the grid is built.
    if (state?.isLoading) {
      instance.beginCustomLoading('');
    }
  }

  /**
   * Run inside Angular, because this is reached from the grid's own store: DevExtreme drives
   * that outside the zone, and everything after the load's `await` stays outside it. The page
   * kept its answer to itself until something unrelated ran change detection — the loading
   * panel sat over a table that had its rows, and Save and + stayed grey after a change that
   * had already been recorded.
   */
  private setMoverGridLoading(key: string, isLoading: boolean): void {
    this.ngZone.run(() => this.applyMoverGridLoading(key, isLoading));
  }

  private applyMoverGridLoading(key: string, isLoading: boolean): void {
    const grid = this.moverGrids.get(key);

    if (isLoading) {
      grid?.beginCustomLoading('');

      return;
    }

    grid?.endCustomLoading();

    // The table has its new rows; the grid still holds the ones it was handed last time. The
    // store gives out what the table has rather than fetching, so a grid that is not asked
    // again goes on showing them — which is what a change of RMD looked like: the title and the
    // totals moved and the rows underneath did not.
    //
    // Asked here rather than beside each thing that reloads a table, because that is a list to
    // be kept complete and it already had a gap in it.
    void grid?.refresh?.();

    // And the only moment the page can be said to have stopped moving of its own accord: the
    // arrangement Save and + are judged against is taken from here.
    this.markArrangementChanged();
  }

  protected async loadMoreMoverRows(
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
    direction: ComparisonTableDirection,
  ): Promise<void> {
    this.hasUserTouchedArrangement = true;
    const state = this.getMoverTableState(datasetKey, level, direction);

    // The first press replaces the page's embedded movers with a paged set of the same size,
    // then grows it — otherwise it would append a second page onto rows it never loaded.
    if (!state.rows.length) {
      await state.reload(() => this.cdr.markForCheck());
    }

    await state.loadMore(() => this.cdr.markForCheck());
  }

  /** Says what the press will do: how many it brings in, and how many remain after that. */
  protected getMoverLoadMoreText(state: ComparisonTableState): string {
    if (state.isLoading) {
      return 'Loading...';
    }

    const remaining = state.remainingCount;
    if (remaining == null) {
      return `Load ${state.pageSize} more`;
    }

    return `Load ${state.nextPageSize} more (${remaining} left)`;
  }

  protected buildMoverTableKey(
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
    direction: ComparisonTableDirection,
  ): string {
    return `${datasetKey}|${level}|${direction}`;
  }

  /** Every mover table describes the comparison the page is showing, so they share its query. */
  private buildRowsBody(datasetKey: ReportingComparisonDatasets, level: 'mga' | 'agent') {
    const filters = toReportFilters(this.appliedReportFilterValues);

    return {
      scope: untracked(this.currentScopeSig) as ReportingComparisonScope,
      current_start_date: this.currentStartDate,
      current_end_date: this.currentEndDate,
      comparison_start_date: this.comparisonStartDate,
      comparison_end_date: this.comparisonEndDate,
      dataset: datasetKey,
      level: level === 'agent' ? ReportingComparisonRowLevels.agent : ReportingComparisonRowLevels.mga,
      ...(filters.mga.length ? { mga: filters.mga } : {}),
      ...(filters.rmd.length ? { rmd: filters.rmd } : {}),
    };
  }

  /**
   * What the table is actually showing: how many rows are loaded, and the column they are
   * ordered by. The old title named the page size, so it said "Top 5" over fifteen rows.
   */
  protected getMoverTableTitle(
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
    direction: ComparisonTableDirection,
    state: ComparisonTableState,
  ): string {
    return getComparisonMoverTitle(
      getComparisonMoverTable(this.getScope(), datasetKey, level, direction),
      state.rows.length,
      { selector: state.sortField, desc: state.sortDescending },
      this.getCaptionRanges(),
    );
  }

  /**
   * The Rows picker now belongs to the table it sits on: each mover table opens at its own size
   * and grows by it, so changing one no longer moves the others or re-requests the page.
   */
  protected onMoverPageSizeChange(
    datasetKey: ReportingComparisonDatasets,
    level: 'mga' | 'agent',
    direction: ComparisonTableDirection,
    count: number,
  ): void {
    const pageSize = this.normalizeTopCount(count);
    const state = this.getMoverTableState(datasetKey, level, direction);

    // The picker reports its value as it is built, which is not somebody choosing a size.
    if (pageSize !== state.pageSize) {
      this.hasUserTouchedArrangement = true;
    }

    void state.setPageSize(pageSize, () => {
      this.cdr.markForCheck();
    });
  }

  protected onQuarterToggle(quarter: QuarterValue, checked: boolean): void {
    const selected = new Set(this.draftSetup.quarters);
    if (checked) {
      selected.add(quarter);
    } else if (selected.size > 1) {
      selected.delete(quarter);
    }

    this.draftSetup.quarters = this.quarterOptions.map(option => option.id).filter(option => selected.has(option));
  }

  protected getOverviewCards(dataset: ReportingComparisonDatasetDto, includeGoalMetrics = true): ComparisonKpiCard[] {
    return buildComparisonSummaryCards(dataset.summary, {
      includeGoalMetrics,
      scope: this.getScope(),
      ranges: this.getCaptionRanges(),
    });
  }

  protected getTrendDataset(
    comparisonData: ReportingComparisonResponseDto | null,
  ): ReportingComparisonDatasetDto | null {
    if (!comparisonData) {
      return null;
    }

    const selectedDataset = this.chartDatasetKey === 'submit' ? comparisonData.submit : comparisonData.paid;
    if (selectedDataset.canDisplayGrid) {
      return selectedDataset;
    }

    return comparisonData.paid.canDisplayGrid
      ? comparisonData.paid
      : comparisonData.submit.canDisplayGrid
        ? comparisonData.submit
        : null;
  }

  protected getAvailableChartDatasetOptions(
    comparisonData: ReportingComparisonResponseDto | null,
  ): SelectOption<ChartDatasetKey>[] {
    if (!comparisonData) {
      return [];
    }

    return this.chartDatasetOptions.filter(option =>
      option.id === 'paid' ? comparisonData.paid.canDisplayGrid : comparisonData.submit.canDisplayGrid,
    );
  }

  protected onChartDatasetChange(value: ChartDatasetKey | null | undefined): void {
    if (value === 'paid' || value === 'submit') {
      this.chartDatasetKey = value;
    }
  }

  protected getMetricCaption(period: 'current' | 'comparison', metric: string): string {
    return getComparisonMetricCaption(period, metric, this.getCaptionRanges());
  }

  protected getPercentCaption(label: string): string {
    return getComparisonPercentCaption(label);
  }

  protected formatCurrency = (value: number | null | undefined): string => formatComparisonCurrency(value);

  protected formatDecimal(value: number | null | undefined): string {
    return formatComparisonDecimal(value);
  }

  protected formatPercent(value: number | null | undefined): string {
    return formatComparisonPercent(value);
  }

  protected formatDeltaCurrency(value: number): string {
    return formatComparisonDeltaCurrency(value);
  }

  protected formatDeltaNumber(value: number): string {
    return formatComparisonDeltaNumber(value);
  }

  protected customizeCurrencyAxisLabel = (arg: { value: number }): string => formatComparisonCurrencyShort(arg.value);

  protected customizeNumberAxisLabel = (arg: { value: number }): string => {
    return this.formatDecimal(arg.value);
  };

  protected customizeCurrencyTooltip = (pointInfo: {
    argumentText: string;
    value: number;
    seriesName: string;
  }): Record<string, string> => {
    return {
      text: `${pointInfo.seriesName}<br/>${pointInfo.argumentText}: ${this.formatCurrency(pointInfo.value)}`,
    };
  };

  protected customizeNumberTooltip = (pointInfo: {
    argumentText: string;
    value: number;
    seriesName: string;
  }): Record<string, string> => {
    return {
      text: `${pointInfo.seriesName}<br/>${pointInfo.argumentText}: ${this.formatDecimal(pointInfo.value)}`,
    };
  };

  protected customizeRankingTooltip = (pointInfo: {
    argumentText: string;
    value: number;
    point?: { data?: { premiumChangePercent?: number | null } };
  }): Record<string, string> => {
    const premiumChangePercent = pointInfo.point?.data?.premiumChangePercent;
    const deltaText = this.formatDeltaCurrency(pointInfo.value);
    const percentText = premiumChangePercent == null ? 'N/A' : this.formatPercent(premiumChangePercent);

    return {
      text: `${pointInfo.argumentText}<br/>Premium Diff: ${deltaText}<br/>Premium %: ${percentText}`,
    };
  };

  protected customizePercentCellText = (cellInfo: { value?: number | null }): string => {
    return this.formatPercent(cellInfo.value);
  };

  protected customizeSummaryEntityText = (itemInfo: { value?: string | null }): string => {
    return itemInfo.value ?? 'Totals';
  };

  /** Whether the permission map has arrived; until then a hidden scope is unknown, not refused. */
  protected readonly isAccessKnownSig = computed(() => this.dashboardContext.permissionsSig() !== null);

  protected readonly isScopeVisibleSig = computed(() =>
    canSeeReportScope(this.dashboardContext, this.currentScopeSig(), Entity.reportsViewerComparison),
  );

  /** Sends the current setup for the current scope, if the viewer may see it. */
  private requestComparison(): void {
    const scope = untracked(this.currentScopeSig);
    if (!scope || !untracked(this.isScopeVisibleSig)) {
      return;
    }

    this.lastRequestedScope = scope;
    // These rows describe the comparison being replaced, so every table re-opens on the new one.
    this.moverTableStates.forEach(state => void state.reload(() => this.cdr.markForCheck()));
    this.comparisonSettled = new Promise<void>(resolve => (this.resolveComparisonSettled = resolve));
    this.query$.next(this.buildQuery(scope));
  }

  private buildQuery(scope: ReportingComparisonScope): ReportingComparisonHttpQuery {
    const filters = toReportFilters(this.appliedReportFilterValues);

    return {
      scope,
      current_start_date: this.currentStartDate,
      current_end_date: this.currentEndDate,
      comparison_start_date: this.comparisonStartDate,
      comparison_end_date: this.comparisonEndDate,
      top_count_mga: this.appliedSetup.mgaTopCount,
      top_count_agent: this.appliedSetup.agentTopCount,
      dataset_selection: ReportingComparisonDatasetSelections.all,
      ...(filters.mga.length ? { mga: filters.mga } : {}),
      ...(filters.rmd.length ? { rmd: filters.rmd } : {}),
    };
  }

  private createDefaultSetup(): ComparisonSetupState {
    const fallbackStopDate = getLastPassedFriday();
    const currentYear = Number(fallbackStopDate.slice(0, 4));
    return {
      mode: 'ytd',
      currentYear,
      compareYear: currentYear - 1,
      stopDate: fallbackStopDate,
      currentRangeStart: `${currentYear}-01-01`,
      currentRangeEnd: fallbackStopDate,
      comparisonRangeStart: `${currentYear - 1}-01-01`,
      comparisonRangeEnd: shiftIsoDateByYears(fallbackStopDate, -1),
      quarters: [quarterFromIsoDate(fallbackStopDate)],
      mgaTopCount: ProductionComparisonDashboardComponent.defaultTopCount,
      agentTopCount: ProductionComparisonDashboardComponent.defaultTopCount,
    };
  }

  private applyDerivedRanges(setup: ComparisonSetupState): void {
    const derivedRanges = this.getDerivedRanges(setup);

    this.currentStartDate = derivedRanges.currentStartDate;
    this.currentEndDate = derivedRanges.currentEndDate;
    this.comparisonStartDate = derivedRanges.comparisonStartDate;
    this.comparisonEndDate = derivedRanges.comparisonEndDate;
  }

  private getDerivedRanges(setup: ComparisonSetupState): {
    currentStartDate: string;
    currentEndDate: string;
    comparisonStartDate: string;
    comparisonEndDate: string;
  } {
    if (setup.mode === 'ytd') {
      const { month, day } = toDateParts(setup.stopDate);
      return {
        currentStartDate: `${setup.currentYear}-01-01`,
        currentEndDate: buildIsoDate(setup.currentYear, month, day),
        comparisonStartDate: `${setup.compareYear}-01-01`,
        comparisonEndDate: buildIsoDate(setup.compareYear, month, day),
      };
    }

    if (setup.mode === 'ranges') {
      return {
        currentStartDate: setup.currentRangeStart,
        currentEndDate: setup.currentRangeEnd,
        comparisonStartDate: setup.comparisonRangeStart,
        comparisonEndDate: setup.comparisonRangeEnd,
      };
    }

    const selectedQuarters: QuarterValue[] = setup.quarters.length ? setup.quarters : ['Q1'];
    const firstQuarter = selectedQuarters[0];
    const lastQuarter = selectedQuarters[selectedQuarters.length - 1];
    const currentRangeStart = quarterRange(setup.currentYear, firstQuarter);
    const currentRangeEnd = quarterRange(setup.currentYear, lastQuarter);
    const comparisonRangeStart = quarterRange(setup.compareYear, firstQuarter);
    const comparisonRangeEnd = quarterRange(setup.compareYear, lastQuarter);

    return {
      currentStartDate: currentRangeStart.start,
      currentEndDate: currentRangeEnd.end,
      comparisonStartDate: comparisonRangeStart.start,
      comparisonEndDate: comparisonRangeEnd.end,
    };
  }

  private toInputDate(value: DateRangeBoxValue, fallback: string): string {
    if (!value) {
      return fallback;
    }

    if (value instanceof Date) {
      return toIsoDate(value);
    }

    return `${value}`.slice(0, 10);
  }

  private clampStopDateToCurrentYear(isoDate: string): string {
    const min = this.getDraftStopDateMin();
    const max = this.getDraftStopDateMax();

    if (isoDate < min) {
      return min;
    }

    if (isoDate > max) {
      return max;
    }

    return isoDate;
  }

  private isRangeOrdered(startDate: string, endDate: string): boolean {
    if (!startDate || !endDate) {
      return true;
    }

    return startDate <= endDate;
  }

  private rangesOverlap(
    currentStartDate: string,
    currentEndDate: string,
    comparisonStartDate: string,
    comparisonEndDate: string,
  ): boolean {
    if (!currentStartDate || !currentEndDate || !comparisonStartDate || !comparisonEndDate) {
      return false;
    }

    return currentStartDate <= comparisonEndDate && comparisonStartDate <= currentEndDate;
  }

  private getAppliedTopCount(level: 'mga' | 'agent'): number {
    return level === 'agent' ? this.appliedSetup.agentTopCount : this.appliedSetup.mgaTopCount;
  }

  /** The scope on screen; the route always carries one by the time anything is drawn. */
  private getScope(): ReportingComparisonScope {
    return this.currentScopeSig() ?? ReportingComparisonScopes.allianceGroup;
  }

  /**
   * The ranges the figures describe, which is what names their columns: the ones the answer on
   * screen was for, or the ones being asked for while it is on its way.
   */
  private getCaptionRanges(): ComparisonRanges {
    const comparisonData = this.comparisonDataSig();

    return {
      currentRange: {
        startDate: comparisonData?.currentRange?.startDate ?? this.currentStartDate,
        endDate: comparisonData?.currentRange?.endDate ?? this.currentEndDate,
      },
      comparisonRange: {
        startDate: comparisonData?.comparisonRange?.startDate ?? this.comparisonStartDate,
        endDate: comparisonData?.comparisonRange?.endDate ?? this.comparisonEndDate,
      },
    };
  }

  private formatMonthDay(isoDate: string): string {
    const [, month, day] = isoDate.split('-');
    return `${month}/${day}`;
  }

  private getAppliedStopDate(): string {
    return this.appliedSetup.mode === 'ytd' ? this.appliedSetup.stopDate : this.currentEndDate;
  }

  private buildAllMgasExportFileName(block: BlockDatasetOptions): string {
    return this.exportBuilder.buildEntitiesFileName('Agencies', block, this.getAppliedStopDate());
  }

  private buildAllAgentsExportFileName(block: BlockDatasetOptions): string {
    const stopDate = this.appliedSetup.mode === 'ytd' ? this.appliedSetup.stopDate : this.currentEndDate;

    return this.exportBuilder.buildEntitiesFileName('Agents', block, stopDate);
  }

  private normalizeTopCount(value: number | null | undefined): number {
    const nextValue = Number(value);
    return this.topCountOptions.some(option => option.id === nextValue)
      ? nextValue
      : ProductionComparisonDashboardComponent.defaultTopCount;
  }

  private getSetupSummary(setup: ComparisonSetupState): string {
    if (setup.mode === 'ytd') {
      return `YTD ${setup.currentYear} vs ${setup.compareYear} through ${this.formatMonthDay(setup.stopDate)}`;
    }

    if (setup.mode === 'ranges') {
      return `Ranges ${formatComparisonCompactDateRange(setup.currentRangeStart, setup.currentRangeEnd)} vs ${formatComparisonCompactDateRange(setup.comparisonRangeStart, setup.comparisonRangeEnd)}`;
    }

    return `Quarter ${setup.quarters.join(', ')} in ${setup.currentYear} vs ${setup.compareYear}`;
  }

  private buildExportFileName(): string {
    return this.exportBuilder.buildWorkbookFileName(this.scopeLabelSig(), this.getDisplayedSetupSummary());
  }

  /** What the builder needs of the page: its title, the ranges that name its columns and what it is showing. */
  private get exportContext(): ComparisonExportContext {
    return {
      pageTitle: this.pageTitleSig(),
      isColumnShown: field => this.isColumnShown(field),
      ranges: this.getCaptionRanges(),
    };
  }
}
